import { ref, computed, shallowRef } from 'vue'
import { defineStore } from 'pinia'
import createEstimatorService from '@/services/createEstimatorService';
import estimatorService from '@/services/estimatorService';
import customerService from '@/services/customerService';
import { useEstimatorSettingsStore } from '@/stores/estimatorSettingsStore';
import orderTemplateService from '@/services/orderTemplateService';
import productCatalogService from '@/services/productCatalogService';
import { useUserStore } from '@/stores/userStore';
import { useGlobalCatalogStore } from '@/stores/globalCatalogStore';
import LeadService from '@/services/leadService';
import {
    ESTIMATE_SOURCE_SYSTEM_ROOF_HUB,
    ESTIMATE_SOURCE_SYSTEM_PRO_LEAD,
    ESTIMATE_SOURCE_SYSTEM_WEATHER_LEAD
} from '@/config/constants';
import {
    resolveLegacySavedOptionForMds,
    findVariantByColorSize,
    normalizeSelectableFeatures
} from '@/utilities/productVariantUtils';

/**
 * COLOR is stored in the legacy format: "Size Color".
 * Older MDS saves used "Color Size". Strip size from either end to get pure color.
 */
function extractPureColorForMds(combinedColor, size) {
    const color = String(combinedColor || '').trim();
    const sizeVal = String(size || '').trim();
    if (!color || !sizeVal) return color;

    const colorLower = color.toLowerCase();
    const sizeLower = sizeVal.toLowerCase();
    if (colorLower.startsWith(`${sizeLower} `)) {
        return color.slice(sizeVal.length).trim();
    }
    if (colorLower.endsWith(` ${sizeLower}`)) {
        return color.slice(0, -(sizeVal.length + 1)).trim();
    }
    return color;
}

export const useCreateEstimateStore = defineStore('create-estimate-store', () => {
    const userStore = useUserStore();
    // ===== STEPS CONFIGURATION =====
    const steps = [{
        step: 1,
        name: "JOB_INFO",
        label: 'createEstimate.jobInfo'
    },
    {
        step: 2,
        name: "BUILD_ESTIMATE",
        label: 'createEstimate.buildEstimate'
    },
    {
        step: 3,
        name: "REVIEW_ESTIMATE",
        label: 'createEstimate.reviewEstimate'
    },
    {
        step: 4,
        name: "BUILD_PROPOSAL",
        label: 'createEstimate.buildProposal'
    }];

    const currentStep = ref(steps[0]);

    // ===== TIERS STATE (matching old Material Cost flow) =====
    // Tier 1 (Good) is selected by default
    // activeTierId and selectedTemplateTier are now in estimatorData
    
    const buildEstimateTiers = ref([
        {
            id: 1,
            title: 'Good',
            displayOrder: 1,
            selected: true, // Default selected tier
            templateId: '0',
            materialCosts: [],
            customMaterialCosts: [],
            materialItemsWithProblems: [],
            chargeValues: { labor: {}, otherCharges: {} },
            customFields: { labor: [], otherCharges: [] },
            calculateCosts: {
                materialCosts: 0,
                customMaterialCosts: 0,
                materialTaxRate: 0,
                materialTax: 0,
                labor: 0,
                otherCharges: 0,
                totalJobCostsSum: 0,
                netProfitGoal: 0,
                overhead: 0,
                totalGrossProfitMargin: 35,
                totalJobCosts: 0,
                totalJobCostsByTotalGpm: 0,
                discountLabel: '',
                discount: 0,
                proposedPrice: 0,
                discountSymbol: '%',
                profitSymbol: '%',
                customProposalPrice: 0,
                customDiscount: 0,
                customDiscountSymbol: '%',
                customProposalPriceIsActive: false,
                customProposedPriceTotal: 0,
                useCustomPrice: false,
                discountType: '%',
                customDiscountType: '%',
                calculatedProfitType: '%'
            },
            pricing: {
                rename: '',
                description: '',
                isSelectedDefault: true,
                isSelectedRolledUp: {
                    isSelected: false,
                    isSelectedDisplayDiscount: false
                },
                isSelectedItemized: {
                    isSelected: false,
                    isSelectedMaterial: true,
                    isSelectedLabor: true,
                    isSelectedOtherCharges: true,
                    isSelectedMaterialHidePricing: true,
                    isSelectedLaborHidePricing: true,
                    isSelectedOtherChargesHidePricing: true
                }
            }
        },
        {
            id: 2,
            title: 'Better',
            displayOrder: 2,
            selected: false,
            templateId: '0',
            materialCosts: [],
            customMaterialCosts: [],
            materialItemsWithProblems: [],
            chargeValues: { labor: {}, otherCharges: {} },
            customFields: { labor: [], otherCharges: [] },
            calculateCosts: {
                materialCosts: 0,
                customMaterialCosts: 0,
                materialTaxRate: 0,
                materialTax: 0,
                labor: 0,
                otherCharges: 0,
                totalJobCostsSum: 0,
                netProfitGoal: 0,
                overhead: 0,
                totalGrossProfitMargin: 35,
                totalJobCosts: 0,
                totalJobCostsByTotalGpm: 0,
                discountLabel: '',
                discount: 0,
                proposedPrice: 0,
                discountSymbol: '%',
                profitSymbol: '%',
                customProposalPrice: 0,
                customDiscount: 0,
                customDiscountSymbol: '%',
                customProposalPriceIsActive: false,
                customProposedPriceTotal: 0,
                useCustomPrice: false,
                discountType: '%',
                customDiscountType: '%',
                calculatedProfitType: '%'
            },
            pricing: {
                rename: '',
                description: '',
                isSelectedDefault: true,
                isSelectedRolledUp: {
                    isSelected: false,
                    isSelectedDisplayDiscount: false
                },
                isSelectedItemized: {
                    isSelected: false,
                    isSelectedMaterial: true,
                    isSelectedLabor: true,
                    isSelectedOtherCharges: true,
                    isSelectedMaterialHidePricing: true,
                    isSelectedLaborHidePricing: true,
                    isSelectedOtherChargesHidePricing: true
                }
            }
        },
        {
            id: 3,
            title: 'Best',
            displayOrder: 3,
            selected: false,
            templateId: '0',
            materialCosts: [],
            customMaterialCosts: [],
            materialItemsWithProblems: [],
            chargeValues: { labor: {}, otherCharges: {} },
            customFields: { labor: [], otherCharges: [] },
            calculateCosts: {
                materialCosts: 0,
                customMaterialCosts: 0,
                materialTaxRate: 0,
                materialTax: 0,
                labor: 0,
                otherCharges: 0,
                totalJobCostsSum: 0,
                netProfitGoal: 0,
                overhead: 0,
                totalGrossProfitMargin: 35,
                totalJobCosts: 0,
                totalJobCostsByTotalGpm: 0,
                discountLabel: '',
                discount: 0,
                proposedPrice: 0,
                discountSymbol: '%',
                profitSymbol: '%',
                customProposalPrice: 0,
                customDiscount: 0,
                customDiscountSymbol: '%',
                customProposalPriceIsActive: false,
                customProposedPriceTotal: 0,
                useCustomPrice: false,
                discountType: '%',
                customDiscountType: '%',
                calculatedProfitType: '%'
            },
            pricing: {
                rename: '',
                description: '',
                isSelectedDefault: true,
                isSelectedRolledUp: {
                    isSelected: false,
                    isSelectedDisplayDiscount: false
                },
                isSelectedItemized: {
                    isSelected: false,
                    isSelectedMaterial: true,
                    isSelectedLabor: true,
                    isSelectedOtherCharges: true,
                    isSelectedMaterialHidePricing: true,
                    isSelectedLaborHidePricing: true,
                    isSelectedOtherChargesHidePricing: true
                }
            }
        }
    ]);

    // Charge calculation helpers
    const getActiveTierChargeValues = () => {
        const tier = buildEstimateTiers.value.find(t => t.id === activeTierId.value);
        return tier?.chargeValues || { labor: {}, otherCharges: {} };
    };

    const getActiveTierCustomFields = () => {
        const tier = buildEstimateTiers.value.find(t => t.id === activeTierId.value);
        return tier?.customFields || { labor: [], otherCharges: [] };
    };

    const calculateTotal = (type) => {
        const tierChargeValues = getActiveTierChargeValues();
        return Object.values(tierChargeValues[type] || {}).reduce((sum, { costPerUnit = 0, quantity = 0 }) =>
            sum + (parseFloat(costPerUnit) || 0) * (parseFloat(quantity) || 0), 0);
    };

    const calculateTierChargeTotal = (tierId, type) => {
        const tier = buildEstimateTiers.value.find(t => t.id === tierId);
        if (!tier) return 0;
        return Object.values(tier.chargeValues[type] || {}).reduce((sum, { costPerUnit = 0, quantity = 0 }) =>
            sum + (parseFloat(costPerUnit) || 0) * (parseFloat(quantity) || 0), 0);
    };

    const getTierChargeTotals = (tierId) => {
        return {
            labor: calculateTierChargeTotal(tierId, 'labor'),
            otherCharges: calculateTierChargeTotal(tierId, 'otherCharges'),
            total: calculateTierChargeTotal(tierId, 'labor') + calculateTierChargeTotal(tierId, 'otherCharges')
        };
    };

    // Helper function to get tier material costs
    const getTierMaterialCosts = (tier) => {
        if (!tier) return { materialCost: 0, customMaterialCost: 0, totalMaterialCost: 0 };
        
        let materialCost = 0;
        if (tier.materialCosts && Array.isArray(tier.materialCosts)) {
            tier.materialCosts.forEach(item => {
                if (item.isCategory) return;
                if (!item.item?.ProductId && !item.catalogInfo?.ProductId) return;
                
                const quantity = parseFloat(item.item?.Quantity) || 0;
                if (quantity < 1) return;
                
                const extPrice = parseFloat(item.pricing?.extPrice) || 
                               (parseFloat(item.pricing?.price) || 0) * quantity ||
                               (parseFloat(item.item?.Price) || 0) * quantity;
                materialCost += extPrice;
            });
        }
        
        let customMaterialCost = 0;
        if (tier.customMaterialCosts && Array.isArray(tier.customMaterialCosts)) {
            tier.customMaterialCosts.forEach(item => {
                const quantity = parseFloat(item.quantity) || 0;
                const price = parseFloat(item.price) || 0;
                customMaterialCost += quantity * price;
            });
        }
        
        return {
            materialCost,
            customMaterialCost,
            totalMaterialCost: materialCost + customMaterialCost
        };
    };

    // Helper function to get tier items with/without quantity
    const getTierItems = (tier) => {
        if (!tier) return { allItems: [], itemsWithQuantity: [], itemsWithNoQuantity: [] };
        
        const materials = tier.materialCosts || [];
        const customs = tier.customMaterialCosts || [];
        
        const validMaterials = materials.filter(item => {
            if (item.isCategory) return false;
            if (!item.item?.ProductId && !item.catalogInfo?.ProductId) return false;
            return true;
        });
        
        const allItems = [...validMaterials, ...customs];
        
        const itemsWithQuantity = allItems.filter(item => {
            const qty = item.item?.Quantity || item.Quantity || item.item?.quantity || item.quantity || 0;
            return qty > 0;
        });
        
        const itemsWithNoQuantity = allItems.filter(item => {
            const qty = item.item?.Quantity || item.Quantity || item.item?.quantity || item.quantity || 0;
            return qty === 0;
        });
        
        return { allItems, itemsWithQuantity, itemsWithNoQuantity };
    };

    // Helper function to calculate tier costs breakdown
    const getTierCostsBreakdown = (tier) => {
        if (!tier) return null;
        
        const costs = tier.calculateCosts || {};
        const materialCosts = getTierMaterialCosts(tier);
        const chargeTotals = getTierChargeTotals(tier.id);
        
        const materialTaxRate = parseFloat(costs.materialTaxRate) || 0;
        const tax = Math.round(materialCosts.totalMaterialCost * (materialTaxRate / 100) * 100) / 100;
        
        // Legacy: Gross Profit Margin = netProfitGoal + overhead (as displayed in ReviewEstimateList.cshtml)
        const netProfitGoal = parseFloat(costs.netProfitGoal) || 0;
        const overhead = parseFloat(costs.overhead) || 0;
        const grossProfitMargin = netProfitGoal + overhead;
        
        return {
            materialCost: materialCosts.materialCost,
            customMaterialCost: materialCosts.customMaterialCost,
            totalMaterialCost: materialCosts.totalMaterialCost,
            tax,
            taxRate: materialTaxRate,
            labor: chargeTotals.labor,
            otherCharges: chargeTotals.otherCharges,
            netProfitGoal,
            overhead,
            grossProfitMargin,
            discount: parseFloat(costs.discount) || 0,
            discountType: costs.discountSymbol || '%',
            discountLabel: costs.discountLabel || '',
            customProposalPrice: parseFloat(costs.customProposalPrice) || 0,
            customProposalPriceIsActive: costs.customProposalPriceIsActive || false,
            customDiscount: parseFloat(costs.customDiscount) || 0,
            customDiscountSymbol: costs.customDiscountSymbol || '%'
        };
    };

    const laborChargesTotal = computed(() => calculateTotal('labor'));
    const otherChargesTotal = computed(() => calculateTotal('otherCharges'));
    const totalCharges = computed(() => laborChargesTotal.value + otherChargesTotal.value);

    // CALCULATE COSTS
    const calculateCosts = ref({
        materialTaxRate: null, 
        netProfitGoal: null,   
        overhead: null,       
        // Discount
        discount: 0,
        discountType: '%', 
        discountLabel: '',
        useCustomPrice: false,
        customProposalPrice: 0,
        customDiscount: 0,
        customDiscountType: '%', 
        calculatedProfitType: '%'
    });

    const getSettingsStore = () => useEstimatorSettingsStore();

    const materialTaxRate = computed(() => {
        const tierCalcCosts = activeTier.value?.calculateCosts;
        if (tierCalcCosts && tierCalcCosts.materialTaxRate !== null) {
            return parseFloat(tierCalcCosts.materialTaxRate) || 0;
        }
        const settingsStore = getSettingsStore();
        const taxRateItem = settingsStore.percentageInputItems?.[0];
        return parseFloat(taxRateItem?.value) || 0;
    });

    const netProfitGoal = computed(() => {
        const tierCalcCosts = activeTier.value?.calculateCosts;
        if (tierCalcCosts && tierCalcCosts.netProfitGoal !== null) {
            return parseFloat(tierCalcCosts.netProfitGoal) || 0;
        }
        const settingsStore = getSettingsStore();
        const netProfitItem = settingsStore.percentageInputItems?.[1];
        return parseFloat(netProfitItem?.value) || 0;
    });

    const overhead = computed(() => {
        const tierCalcCosts = activeTier.value?.calculateCosts;
        if (tierCalcCosts && tierCalcCosts.overhead !== null) {
            return parseFloat(tierCalcCosts.overhead) || 0;
        }
        const settingsStore = getSettingsStore();
        const overheadItem = settingsStore.percentageInputItems?.[2];
        return parseFloat(overheadItem?.value) || 0;
    });

    const getCustomMaterialCostTotal = () => {
        const tier = activeTier.value;
        if (!tier?.customMaterialCosts) return 0;
        return tier.customMaterialCosts
            .filter(item => (parseFloat(item.quantity) || 0) > 0)
            .reduce((acc, item) => acc + (parseFloat(item.price) || 0) * (parseFloat(item.quantity) || 0), 0);
    };

    const materialTax = computed(() => {
        const costs = materialCostTotal.value + getCustomMaterialCostTotal();
        const taxRate = materialTaxRate.value;
        return Math.round(costs * (taxRate / 100) * 100) / 100;
    });

    const totalJobCosts = computed(() => {
        const material = materialCostTotal.value + getCustomMaterialCostTotal();
        const tax = materialTax.value;
        const labor = laborChargesTotal.value;
        const other = otherChargesTotal.value;
        return material + tax + labor + other;
    });

    const totalGrossProfitMargin = computed(() => {
        return netProfitGoal.value + overhead.value;
    });

    // Gross Profit using legacy formula: Total Job Costs / 100 * Total Gross Profit Margin
    // This matches the calculation: totalJobCostsByTotalGpm = Math.round(totalJobCosts / 100 * totalGrossProfitMargin * 100) / 100
    const grossProfit = computed(() => {
        const jobCosts = totalJobCosts.value;
        const margin = totalGrossProfitMargin.value;
        
        // Legacy formula: totalJobCosts / 100 * totalGrossProfitMargin (then round)
        const profit = jobCosts / 100 * margin;
        
        return Math.round(profit * 100) / 100;
    });

    // Proposal Price using legacy formula: totalJobCosts + grossProfit (rounded) - discount  
    // proposedPrice = totalJobCosts + totalJobCostsByTotalGpm; then apply discount
    const proposalPrice = computed(() => {
        const jobCosts = totalJobCosts.value;
        let price = jobCosts + grossProfit.value;
        
        // Apply discount from active tier
        const tierCalcCosts = activeTier.value?.calculateCosts;
        if (tierCalcCosts) {
            const discount = parseFloat(tierCalcCosts.discount) || 0;
            if (discount > 0) {
                if (tierCalcCosts.discountSymbol === '%' || tierCalcCosts.discountType === '%') {
                    const discountAmount = (price / 100) * discount;
                    price = price - discountAmount;
                } else {
                    const roundedDiscount = Math.round(discount * 100) / 100;
                    price = price - roundedDiscount;
                }
            }
        }
        
        return Math.max(0, Math.round(price * 100) / 100);
    });

    // Custom Proposal Total with custom discount applied
    const customProposalTotal = computed(() => {
        const tierCalcCosts = activeTier.value?.calculateCosts;
        if (!tierCalcCosts) return 0;
        
        const customPrice = parseFloat(tierCalcCosts.customProposalPrice) || 0;
        const discount = parseFloat(tierCalcCosts.customDiscount) || 0;
        
        let total;
        // Check both field names for backward compatibility
        const discountType = tierCalcCosts.customDiscountSymbol || tierCalcCosts.customDiscountType || '%';
        if (discountType === '%') {
            total = customPrice * ((100 - discount) / 100);
        } else {
            const roundedDiscount = Math.round(discount * 100) / 100;
            total = customPrice - roundedDiscount;
        }
        
        return Math.max(0, Math.round(total * 100) / 100);
    });

    // Calculated Profit for custom proposal
    const calculatedProfit = computed(() => {
        const tierCalcCosts = activeTier.value?.calculateCosts;
        if (!tierCalcCosts) return 0;
        
        const customPrice = parseFloat(tierCalcCosts.customProposalPrice) || 0;
        const customDiscount = parseFloat(tierCalcCosts.customDiscount) || 0;
        const jobCosts = totalJobCosts.value;
        
        if (customPrice <= 0) return 0;
        
        // Check both field names for backward compatibility
        const profitType = tierCalcCosts.profitSymbol || tierCalcCosts.calculatedProfitType || '%';
        const discountType = tierCalcCosts.customDiscountSymbol || tierCalcCosts.customDiscountType || '%';
     
        let discountAmount;
        if (discountType === '%') {
            discountAmount = customPrice * customDiscount / 100;
        } else {
            discountAmount = customDiscount;
        }
        
        if (profitType === '%') {
            // formula: ((customProposalPrice - (totalJobCosts + discountAmount)) / customProposalPrice) * 100
            
            const rawProfit = ((customPrice - (jobCosts + discountAmount)) / customPrice) * 100;
            return Math.floor(rawProfit * 100) / 100;
        } else {
            const rawProfit = customPrice - (jobCosts + discountAmount);
            return Math.floor(rawProfit * 100) / 100;
        }
    });

    const updateCalculateCosts = (field, value) => {
        if (field in calculateCosts.value) {
            calculateCosts.value[field] = value;
        }
    };

    const resetCalculateCosts = () => {
        calculateCosts.value = {
            materialTaxRate: null,
            netProfitGoal: null,
            overhead: null,
            discount: 0,
            discountType: '%',
            discountLabel: '',
            useCustomPrice: false,
            customProposalPrice: 0,
            customDiscount: 0,
            customDiscountType: '%',
            calculatedProfitType: '%'
        };
    };

    const toggleCustomProposalPrice = (tierId, enabled) => {
        const tier = buildEstimateTiers.value.find(t => t.id === tierId);
        if (!tier) return;

        tier.calculateCosts.useCustomPrice = enabled;
        // Keep legacy-compatible active flag in sync with checkbox state.
        tier.calculateCosts.customProposalPriceIsActive = enabled;

        if (enabled) {
            // When enabled: switch to Rolled Up with Display Discount
            tier.pricing.isSelectedDefault = false;
            tier.pricing.isSelectedRolledUp.isSelected = true;
            tier.pricing.isSelectedRolledUp.isSelectedDisplayDiscount = true;
            tier.pricing.isSelectedItemized.isSelected = false;
            
            // Uncheck Material/Labor/OtherCharges but leave HidePricing unchanged
            tier.pricing.isSelectedItemized.isSelectedMaterial = false;
            tier.pricing.isSelectedItemized.isSelectedLabor = false;
            tier.pricing.isSelectedItemized.isSelectedOtherCharges = false;
            
            // For OTHER tiers where custom price is NOT checked, set to Default
            buildEstimateTiers.value.forEach(otherTier => {
                if (otherTier.id !== tier.id && !otherTier.calculateCosts.useCustomPrice) {
                    otherTier.pricing.isSelectedDefault = true;
                    otherTier.pricing.isSelectedRolledUp.isSelected = false;
                    otherTier.pricing.isSelectedItemized.isSelected = false;
                }
            });
        } else {
            tier.pricing.isSelectedRolledUp.isSelectedDisplayDiscount = false;
        }
    };

    const updateTierCalculateCostsField = (tierId, field, value) => {
        const tier = buildEstimateTiers.value.find(t => t.id === tierId);
        if (!tier || !tier.calculateCosts) return;

        // Map component field names to store field names and update both for backwards compatibility
        if (field === 'discountType') {
            tier.calculateCosts.discountSymbol = value;
            tier.calculateCosts.discountType = value;
        } else if (field === 'customDiscountType') {
            tier.calculateCosts.customDiscountSymbol = value;
            tier.calculateCosts.customDiscountType = value;
        } else if (field === 'calculatedProfitType') {
            tier.calculateCosts.profitSymbol = value;
            tier.calculateCosts.calculatedProfitType = value;
        } else {
            // For other fields, update directly
            tier.calculateCosts[field] = value;
        }
    };

    const getChargeValue = (type, itemId, field) => {
        const tier = buildEstimateTiers.value.find(t => t.id === activeTierId.value);
        if (!tier) return 0;
        
        if (!tier.chargeValues[type][itemId]) {
            tier.chargeValues[type][itemId] = { costPerUnit: 0, quantity: 0 };
        }
        return tier.chargeValues[type][itemId][field];
    };

    const updateChargeValue = (type, itemId, field, value) => {
        const tier = buildEstimateTiers.value.find(t => t.id === activeTierId.value);
        if (!tier) return;
        
        if (!tier.chargeValues[type][itemId]) {
            tier.chargeValues[type][itemId] = { costPerUnit: 0, quantity: 0 };
        }
        tier.chargeValues[type][itemId][field] = parseFloat(value) || 0;
    };

    const addCustomField = (type) => {
        const tier = buildEstimateTiers.value.find(t => t.id === activeTierId.value);
        if (!tier) return null;
        
        const fields = tier.customFields[type];
        const maxOrder = fields.length > 0 ? Math.max(...fields.map(f => f.displayOrder || 0)) : 1000;
        const id = `custom-${Date.now()}`;

        fields.push({ id, name: '', displayOrder: maxOrder + 1, saved: false });
        tier.chargeValues[type][id] = { costPerUnit: 0, quantity: 0 };
        return id;
    };

    const updateCustomFieldName = (type, id, name) => {
        const tier = buildEstimateTiers.value.find(t => t.id === activeTierId.value);
        if (!tier) return;
        
        const field = tier.customFields[type].find(f => f.id === id);
        if (field) field.name = name;
    };

    const deleteCustomField = (type, id) => {
        const tier = buildEstimateTiers.value.find(t => t.id === activeTierId.value);
        if (!tier) return;
        
        tier.customFields[type] = tier.customFields[type].filter(f => f.id !== id);
        delete tier.chargeValues[type][id];
    };

    const getActiveCustomFields = (type) => {
        const tier = buildEstimateTiers.value.find(t => t.id === activeTierId.value);
        return tier?.customFields[type] || [];
    };

    // Get active tier (handles both string and number IDs)
    const activeTier = computed(() => {
        const numericId = Number(activeTierId.value);
        return buildEstimateTiers.value.find(t => t.id === numericId) || buildEstimateTiers.value[0];
    });

    // Set active tier (handles both string and number IDs)
    const setActiveTier = (tierId) => {
        const numericId = Number(tierId);
        // Update selected flag on all tiers
        buildEstimateTiers.value.forEach(tier => {
            tier.selected = tier.id === numericId;
        });
        activeTierId.value = numericId;
    };

    // Get tier by ID (handles both string and number IDs)
    const getTierById = (tierId) => {
        const numericId = Number(tierId);
        return buildEstimateTiers.value.find(t => t.id === numericId);
    };

    // Set material costs for a specific tier
    const setTierMaterialCosts = (tierId, costs) => {
        const tier = getTierById(tierId);
        if (tier) {
            tier.materialCosts = costs;
        }
    };

    // Set custom material costs for a specific tier
    const setTierCustomMaterialCosts = (tierId, costs) => {
        const tier = getTierById(tierId);
        if (tier) {
            tier.customMaterialCosts = costs;
        }
    };

    // Set charge values (labor/other charges) for a specific tier
    const setTierChargeValues = (tierId, values) => {
        const tier = getTierById(tierId);
        if (tier) {
            tier.chargeValues = values;
        }
    };

    // Set custom fields (labor/other charges) for a specific tier
    const setTierCustomFields = (tierId, fields) => {
        const tier = getTierById(tierId);
        if (tier) {
            tier.customFields = fields;
        }
    };

    // ===== ACTIVE TIER MATERIAL COSTS (proxies to active tier's arrays) =====
    // This matches old code behavior where each tier has its own materialCosts
    const activeTierMaterialCosts = computed({
        get: () => {
            const tier = activeTier.value;
            return tier ? tier.materialCosts : [];
        },
        set: (value) => {
            const tier = activeTier.value;
            if (tier) {
                tier.materialCosts = value;
            }
        }
    });

    const activeTierCustomMaterialCosts = computed({
        get: () => {
            const tier = activeTier.value;
            return tier ? tier.customMaterialCosts : [];
        },
        set: (value) => {
            const tier = activeTier.value;
            if (tier) {
                tier.customMaterialCosts = value;
            }
        }
    });

     const activeTierChargeValues = computed({
        get: () => {
            const tier = activeTier.value;
            return tier ? tier.chargeValues : { labor: {}, otherCharges: {} };
        },
        set: (value) => {
            const tier = activeTier.value;
            if (tier) {
                tier.chargeValues = value;
            }
        }
    });

    const activeTierCustomFields = computed({
        get: () => {
            const tier = activeTier.value;
            return tier ? tier.customFields : { labor: [], otherCharges: [] };
        },
        set: (value) => {
            const tier = activeTier.value;
            if (tier) {
                tier.customFields = value;
            }
        }
    });


    // Set template ID for a tier
    const setTierTemplateId = (tierId, templateId) => {
        const tier = getTierById(tierId);
        if (tier) {
            tier.templateId = templateId;
        }
    };

    // ===== MATERIAL COSTS STATE =====
    // Material costs are now in estimatorData

    const resetEstimate = () => {
        currentStep.value = steps[0];
        materialCosts.value = [];
        customMaterialCosts.value = [];
        materialCostValidationErrors.value = [];
        materialCostsBranchCode.value = '';
        materialCostsJobAccountId.value = '';
        
        // Reset tiers to default state (Tier 1 Good selected by default)
        activeTierId.value = 1;
        selectedTemplateTier.value = 1; // Reset to Good tier
        buildEstimateTiers.value.forEach(tier => {
            tier.selected = tier.id === 1;
            tier.templateId = '0';
            tier.materialCosts = [];
            tier.customMaterialCosts = [];
            tier.materialItemsWithProblems = [];
            tier.chargeValues = { labor: {}, otherCharges: {} };
            tier.customFields = { labor: [], otherCharges: [] };
            tier.calculateCosts = {
                materialCosts: 0,
                customMaterialCosts: 0,
                materialTaxRate: 0,
                materialTax: 0,
                labor: 0,
                otherCharges: 0,
                totalJobCostsSum: 0,
                netProfitGoal: 0,
                overhead: 0,
                totalGrossProfitMargin: 35,
                totalJobCosts: 0,
                totalJobCostsByTotalGpm: 0,
                discountLabel: '',
                discount: 0,
                proposedPrice: 0,
                discountSymbol: '%',
                profitSymbol: '%',
                customProposalPrice: 0,
                customDiscount: 0,
                customDiscountSymbol: '%',
                customProposalPriceIsActive: false,
                customProposedPriceTotal: 0
            };
        });
    }

    // ===== ESTIMATOR DATA =====
    const estimatorData = ref({
        loading: {
            isLoading: false,
            message: '',
            loadingBranches: false,
            loadingJobAccounts: false,
            loadingRoofPitch: false,
            savingDraft: false,
            loadingTemplates: false
        },
        ui: {
            isManualAddress: false,
            showJobInfoValidationError: false
        },
        validation: {
            addressErrors: {},
            jobInfoErrors: {},
            materialCostValidationErrors: []
        },
        shipToAddressesForBranches: [],
        shipToAddresses: [],
        selectedBranchId: null,
        oldBranchId: null,
        newBranchId: null,
        newBranchCode: null,
        estimateSettings: null,
        estimateId: null,
        roofHubEstimateNumber: 0,
        selectedBranchCode: '',
        proposalUniqueID: '',
        statusId: 6, // RH.Enums.EstimateStatus.Draft
        propertyReportId: '',
        activeTierId: 1, // Default to Good tier
        selectedTemplateTier: 1,
        materialCosts: [],
        customMaterialCosts: [],
        materialCostsBranchCode: '',
        materialCostsJobAccountId: '',
        templates: {
            categories: [],
            all: [],
            tierTemplates: {
                good: [],
                better: [],
                best: []
            },
            branchCode: '',
            selected: {
                good: null,
                better: null,
                best: null
            }
        },
        jobInformation: {
            jobName: '',
            jobAccount: '',
            contactName: '',
            selectedBranch: '',
            jobAccountName: '',
            branchName: '',
            roofPitch: '',
            shipToAddress1: '',
            shipToAddress2: '',
            shipToAddressCity: '',
            shipToAddressState: '',
            shipToAddressZipCode: '',
            latitude: null,
            longitude: null,
            jobAccounts: [],
            roofPitches: [],
            branchList: []
        },
        buildEstimate: {
            // Note: Tiers are stored separately as buildEstimateTiers ref for reactivity
            labor: [],
            otherCharges: []
        },
        buildProposalData: {
            sectionDisplayOrder: [],
            photos: {
                isSelected: true,
                content: []
            },
            testimonials: {
                isSelected: true,
                content: []
            },
            manufacturerSellSheets: {
                isSelected: false,
                content: []
            },
            aboutUs: {
                isSelected: true,
                content: ""
            },
            pricing: {
                isSelected: true,
                disabledCheckbox: true,
                tiers: [],
                subtitle: 'Wish to rename the Good/Better/Best tiers? Enter your preferred tier name and/or a brief description if there is added tier-specific context you want to include in your proposal.'
            },
            TermsAndConditions: {
                isSelected: true,
                content: ''
            },
            ScopeOfWork: {
                isSelected: true,
                content: ''
            },
            customNotes: {
                isSelected: true,
                content: "",
                placeholder: "Enter your custom notes..."
            },
            eSign: {
                isSelected: true,
                disableCheckBox: true,
                envelopeId: null,
                homeOwner: {
                    email: '',
                    firstname: '',
                    lastname: ''
                },
                companyRepresentative: {
                    email: userStore.userDetails?.Email || '',
                    firstname: userStore.userDetails?.FirstName || '',
                    lastname: userStore.userDetails?.LastName || ''
                }
            },
            itemizedPricing: {
                isSelected: false,
                isSelectedMaterialCost: false,
                isSelectedTax: false,
                isSelectedLabor: false,
                isSelectedOtherCharges: false,
                isSelectedDiscount: false
            },
            coverPageLogoContactInfo: {
                isSelected: true,
                imageCompanyLogo: {
                    companyLogo: []
                },
                coverPage: {
                    customCoverImageURI: "",
                    imageCoverPage: []
                },
                contactInformation: {
                    isSelectedAddress: true,
                    address: "",
                    isSelectedEmailAddress: true,
                    emailAddress: "",
                    isSelectedPhoneNumber: true,
                    phoneNumber: "",
                }
            },
            propertyReport: {
                reportId: null,
                reportData: null,
                loading: false
            }
        },
        measurementCategory: {},
        aerialData: {
            aerialReportProvider: '',
            aerialReportReferenceLink: '',
            aerialReportReferenceNumber: ''
        }
    });

    const buildProposalData = computed(() => estimatorData.value.buildProposalData);

    // ===== SAVED ESTIMATE API RESPONSE =====
    const savedEstimateResponse = ref(null);

    // ===== BUILD PROPOSAL CHANGE TRACKING =====
    let buildProposalInitialState = null;

    const captureBuildProposalInitialState = () => {
        buildProposalInitialState = {
            buildProposalData: JSON.parse(JSON.stringify(estimatorData.value.buildProposalData))
        };
    };

    const hasBuildProposalChanges = () => {
        if (!buildProposalInitialState) return false;
        const currentState = { buildProposalData: estimatorData.value.buildProposalData };
        const hasChanges = JSON.stringify(currentState) !== JSON.stringify(buildProposalInitialState);
        return hasChanges;
    };

    const validateBuildProposal = () => {
        const errors = [];
        // Note: About Us content is optional - remove validation to match legacy behavior
        // Users can save/preview proposals without filling in About Us
        
        return {
            isValid: errors.length === 0,
            errors: errors
        };
    };

    // Additional root-level fields
    const showUnsavedDataDialog = ref(false);
    const crcOfLastSave = ref(null);
    const crcOfLastCalculate = ref(null);
    
    // Track the closest branch code (first branch in the sorted list)
    const isClosestBranchSelected = ref(null);
    
    // ===== BRANCH CHANGE AVAILABILITY STATE =====
    const branchChangeState = ref({
        isChecking: false,
        pendingBranchCode: null,
        previousBranchCode: null,
        unavailableProducts: {},
        productsResponseCache: {},
        skipNextWatchTrigger: false
    });

    // Legacy compatibility refs - point to estimatorData properties
    const address = computed({
        get: () => ({
            shipToAddress1: estimatorData.value.jobInformation.shipToAddress1,
            shipToAddress2: estimatorData.value.jobInformation.shipToAddress2,
            shipToAddressCity: estimatorData.value.jobInformation.shipToAddressCity,
            shipToAddressState: estimatorData.value.jobInformation.shipToAddressState,
            shipToAddressZipCode: estimatorData.value.jobInformation.shipToAddressZipCode,
            latitude: estimatorData.value.jobInformation.latitude || null,
            longitude: estimatorData.value.jobInformation.longitude || null
        }),
        set: (val) => {
            estimatorData.value.jobInformation.shipToAddress1 = val.shipToAddress1 || '';
            estimatorData.value.jobInformation.shipToAddress2 = val.shipToAddress2 || '';
            estimatorData.value.jobInformation.shipToAddressCity = val.shipToAddressCity || '';
            estimatorData.value.jobInformation.shipToAddressState = val.shipToAddressState || '';
            estimatorData.value.jobInformation.shipToAddressZipCode = val.shipToAddressZipCode || '';
            // Store latitude and longitude if provided
            if (val.latitude !== undefined) {
                estimatorData.value.jobInformation.latitude = val.latitude;
            }
            if (val.longitude !== undefined) {
                estimatorData.value.jobInformation.longitude = val.longitude;
            }
        }
    });

    const jobInformation = computed({
        get: () => ({
            branchCode: estimatorData.value.jobInformation.selectedBranch,
            jobAccountId: estimatorData.value.jobInformation.jobAccount,
            jobName: estimatorData.value.jobInformation.jobName,
            roofPitchId: estimatorData.value.jobInformation.roofPitch,
            contactName: estimatorData.value.jobInformation.contactName
        }),
        set: (val) => {
            estimatorData.value.jobInformation.selectedBranch = val.branchCode || '';
            estimatorData.value.jobInformation.jobAccount = val.jobAccountId || '';
            estimatorData.value.jobInformation.jobName = val.jobName || '';
            estimatorData.value.jobInformation.roofPitch = val.roofPitchId || '';
            estimatorData.value.jobInformation.contactName = val.contactName || '';
        }
    });

    const branches = computed({
        get: () => estimatorData.value.jobInformation.branchList,
        set: (val) => { estimatorData.value.jobInformation.branchList = val; }
    });

    const jobAccounts = computed({
        get: () => estimatorData.value.jobInformation.jobAccounts,
        set: (val) => { estimatorData.value.jobInformation.jobAccounts = val; }
    });

    const roofPitchValues = computed({
        get: () => estimatorData.value.jobInformation.roofPitches,
        set: (val) => { estimatorData.value.jobInformation.roofPitches = val; }
    });

    const estimateId = computed({
        get: () => estimatorData.value.estimateId,
        set: (val) => { estimatorData.value.estimateId = val; }
    });

    const estimateNumber = computed({
        get: () => estimatorData.value.roofHubEstimateNumber,
        set: (val) => { estimatorData.value.roofHubEstimateNumber = val; }
    });

    const estimateStatus = computed({
        get: () => estimatorData.value.statusId,
        set: (val) => { estimatorData.value.statusId = val; }
    });

    // Loading state computed properties
    const isManualAddress = computed({
        get: () => estimatorData.value.ui.isManualAddress,
        set: (val) => { estimatorData.value.ui.isManualAddress = val; }
    });

    const loadingBranches = computed({
        get: () => estimatorData.value.loading.loadingBranches,
        set: (val) => { estimatorData.value.loading.loadingBranches = val; }
    });

    const loadingJobAccounts = computed({
        get: () => estimatorData.value.loading.loadingJobAccounts,
        set: (val) => { estimatorData.value.loading.loadingJobAccounts = val; }
    });

    const loadingRoofPitch = computed({
        get: () => estimatorData.value.loading.loadingRoofPitch,
        set: (val) => { estimatorData.value.loading.loadingRoofPitch = val; }
    });

    const savingDraft = computed({
        get: () => estimatorData.value.loading.savingDraft,
        set: (val) => { estimatorData.value.loading.savingDraft = val; }
    });

    const loadingTemplates = computed({
        get: () => estimatorData.value.loading.loadingTemplates,
        set: (val) => { estimatorData.value.loading.loadingTemplates = val; }
    });

    // Validation computed properties
    const addressErrors = computed({
        get: () => estimatorData.value.validation.addressErrors,
        set: (val) => { estimatorData.value.validation.addressErrors = val; }
    });

    const jobInfoErrors = computed({
        get: () => estimatorData.value.validation.jobInfoErrors,
        set: (val) => { estimatorData.value.validation.jobInfoErrors = val; }
    });

    const showJobInfoValidationError = computed({
        get: () => estimatorData.value.ui.showJobInfoValidationError,
        set: (val) => { estimatorData.value.ui.showJobInfoValidationError = val; }
    });

    // Material costs computed properties
    const materialCosts = computed({
        get: () => estimatorData.value.materialCosts,
        set: (val) => { estimatorData.value.materialCosts = val; }
    });

    const customMaterialCosts = computed({
        get: () => estimatorData.value.customMaterialCosts,
        set: (val) => { estimatorData.value.customMaterialCosts = val; }
    });

    const materialCostValidationErrors = computed({
        get: () => estimatorData.value.validation.materialCostValidationErrors,
        set: (val) => { estimatorData.value.validation.materialCostValidationErrors = val; }
    });

    const materialCostsBranchCode = computed({
        get: () => estimatorData.value.materialCostsBranchCode,
        set: (val) => { estimatorData.value.materialCostsBranchCode = val; }
    });

    const materialCostsJobAccountId = computed({
        get: () => estimatorData.value.materialCostsJobAccountId,
        set: (val) => { estimatorData.value.materialCostsJobAccountId = val; }
    });

    // Tier state computed properties
    const activeTierId = computed({
        get: () => estimatorData.value.activeTierId,
        set: (val) => { estimatorData.value.activeTierId = val; }
    });

    const selectedTemplateTier = computed({
        get: () => estimatorData.value.selectedTemplateTier,
        set: (val) => { estimatorData.value.selectedTemplateTier = val; }
    });

    // Template computed properties
    const estimatorSettings = computed({
        get: () => estimatorData.value.estimateSettings,
        set: (val) => { estimatorData.value.estimateSettings = val; }
    });

    const templateCategories = computed({
        get: () => estimatorData.value.templates.categories,
        set: (val) => { estimatorData.value.templates.categories = val; }
    });

    const allTemplates = computed({
        get: () => estimatorData.value.templates.all,
        set: (val) => { estimatorData.value.templates.all = val; }
    });

    const tierTemplates = computed({
        get: () => estimatorData.value.templates.tierTemplates,
        set: (val) => { estimatorData.value.templates.tierTemplates = val; }
    });

    const templatesBranchCode = computed({
        get: () => estimatorData.value.templates.branchCode,
        set: (val) => { estimatorData.value.templates.branchCode = val; }
    });

    const selectedTierTemplates = computed({
        get: () => estimatorData.value.templates.selected,
        set: (val) => { estimatorData.value.templates.selected = val; }
    });

    // ===== COMPUTED =====
    const hasValidAddress = computed(() => {
        return createEstimatorService.validateAddress(address.value);
    });

    const hasValidJobInfo = computed(() => {
        return createEstimatorService.validateJobInformation(jobInformation.value);
    });

    const canProceedToNextStep = computed(() => {
        if (currentStep.value.step === 1) {
            return hasValidAddress.value && hasValidJobInfo.value;
        }
        return false;
    });

    const fullAddress = computed(() => {
        return createEstimatorService.formatFullAddress(address.value);
    });

    const hasUnsavedChanges = computed(() => {
        return !!(
            address.value.shipToAddress1 ||
            jobInformation.value.branchCode ||
            jobInformation.value.jobAccountId ||
            jobInformation.value.jobName
        );
    });

    // Material Cost validation - check if at least one product exists (matching old UI logic)
    // Check across ALL tiers, not just active tier - allows proceeding to review if any tier has items
    const hasAtLeastOneProduct = computed(() => {
        return buildEstimateTiers.value.some(tier => {
            const tierMaterials = tier.materialCosts || [];
            return tierMaterials.some(item => item.isCategory !== true && item.item?.ProductId);
        });
    });

    const uomValidationErrorCount = ref(0);
    const hasUomValidationError = computed(() => uomValidationErrorCount.value > 0);
    
    const incrementUomValidationError = () => {
        uomValidationErrorCount.value++;
    };
    
    const decrementUomValidationError = () => {
        if (uomValidationErrorCount.value > 0) {
            uomValidationErrorCount.value--;
        }
    };
    
    const resetUomValidationErrorCount = () => {
        uomValidationErrorCount.value = 0;
    };

    // Get only product rows (non-category items) from active tier
    const materialCostProducts = computed(() => {
        const tierMaterials = activeTierMaterialCosts.value;
        return tierMaterials.filter(item => !item.isCategory);
    });

    // Calculate total extended price for all material costs in active tier (auto-updates on any change)
    const materialCostTotal = computed(() => {
        let total = 0;
        const tierMaterials = activeTierMaterialCosts.value;
        tierMaterials.forEach(item => {
            if (item.isCategory) return;
            
            const quantity = item.item?.Quantity || 0;
            // Only count items with quantity >= 1
            if (quantity < 1) return;
            
            // Use pricing.extPrice if available
            if (item.pricing?.extPrice) {
                total += parseFloat(item.pricing.extPrice) || 0;
            } else if (item.pricing?.price) {
                // Fallback: calculate from price * quantity
                total += (parseFloat(item.pricing.price) || 0) * quantity;
            }
        });
        return total;
    });

    // Formatted total for display
    const formattedMaterialCostTotal = computed(() => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2
        }).format(materialCostTotal.value);
    });

    // Can proceed to Review Estimate step
    const canProceedToReviewEstimate = computed(() => {
        return hasAtLeastOneProduct.value;
    });

    // ===== ADDRESS METHODS =====
    const setAddress = (newAddress) => {
        address.value = { ...address.value, ...newAddress };
    };

    const resetAddress = () => {
        address.value = {
            shipToAddress1: '',
            shipToAddress2: '',
            shipToAddressCity: '',
            shipToAddressState: '',
            shipToAddressZipCode: '',
            latitude: null,
            longitude: null
        };
        clearAddressErrors();
    };

    const setManualAddressMode = (isManual) => {
        isManualAddress.value = isManual;
    };

    const setAddressErrors = (errors) => {
        addressErrors.value = { ...addressErrors.value, ...errors };
    };

    const clearAddressErrors = () => {
        addressErrors.value = {
            shipToAddress1: false,
            shipToAddressCity: false,
            shipToAddressState: false,
            shipToAddressZipCode: false
        };
    };

    // ===== JOB INFORMATION METHODS =====
    const setJobInformation = (data) => {
        jobInformation.value = { ...jobInformation.value, ...data };
    };

    const resetJobInformation = () => {
        jobInformation.value = {
            branchCode: '',
            jobAccountId: '',
            jobName: '',
            roofPitchId: '',
            contactName: ''
        };
        clearJobInfoErrors();
    };

    const setJobInfoErrors = (errors) => {
        jobInfoErrors.value = { ...jobInfoErrors.value, ...errors };
    };

    const clearJobInfoErrors = () => {
        jobInfoErrors.value = {
            branchCode: false,
            jobAccountId: false,
            jobName: false
        };
    };

    // ===== BRANCH METHODS =====
    const setBranches = (branchList) => {
        branches.value = branchList || [];
    };

    const selectDefaultBranch = (userDetails, eligibleBranches) => {
        if (!eligibleBranches || eligibleBranches.length === 0) return null;

        const defaultDeliveryBranch = userDetails?.UserSettings?.DefaultDeliveryBranch;

        // Check if DefaultDeliveryBranch is set to "HomeBranch"
        if (defaultDeliveryBranch === "HomeBranch") {
            if (userDetails?.HomeBranch) {
                const homeBranch = eligibleBranches.find(
                    b => b.BranchCode === userDetails.HomeBranch
                );
                if (homeBranch) {
                    return homeBranch.BranchCode;
                }
            }
        }
        // Check if DefaultDeliveryBranch is set to "ClosestBranch"
        else if (defaultDeliveryBranch === "ClosestBranch") {
            isClosestBranchSelected.value = true;
            return eligibleBranches[0]?.BranchCode || null;
        }
        // Otherwise, use DefaultDeliveryBranch as actual branch code
        else if (defaultDeliveryBranch) {
            const defaultBranch = eligibleBranches.find(
                b => b.BranchCode === defaultDeliveryBranch
            );
            if (defaultBranch) {
                return defaultBranch.BranchCode;
            }
        }

        // Fallback: Priority 2 - HomeBranch
        if (userDetails?.HomeBranch) {
            const homeBranch = eligibleBranches.find(
                b => b.BranchCode === userDetails.HomeBranch
            );
            if (homeBranch) {
                return homeBranch.BranchCode;
            }
        }
            isClosestBranchSelected.value = true;
        // Fallback: Priority 3 - Closest branch (first in list)
        return eligibleBranches[0]?.BranchCode || null;
    };

    const filterEligibleBranches = (branchResults, branchShipToMap) => {
        if (!branchResults || !Array.isArray(branchResults)) return [];
        
        return branchResults.filter(
            (branch) =>
                branchShipToMap &&
                branchShipToMap[branch.BranchCode] &&
                branch.IsBranchAvailableForOrders
        );
    };

    const getProductIdFromItem = (item) => {
        return item?.item?.ProductId || item?.catalogInfo?.ProductId || item?.ProductId || item?.ProductID || item?.productId;
    };

    const hasMaterialCostItems = () => {
        if (!buildEstimateTiers.value || !Array.isArray(buildEstimateTiers.value)) {
            return false;
        }

        return buildEstimateTiers.value.some(tier => {
            const materials = tier.materialCosts || [];
            const validMaterials = materials.filter(item => !item.isCategory && getProductIdFromItem(item));
            const customMaterials = tier.customMaterialCosts || [];
            const validCustom = customMaterials.filter(item => getProductIdFromItem(item));

            return validMaterials.length > 0 || validCustom.length > 0;
        });
    };

    const checkBranchChangeAvailability = async (newBranchCode) => {
        try {
            branchChangeState.value.isChecking = true;
            branchChangeState.value.unavailableProducts = {};
            branchChangeState.value.productsResponseCache = {};

            if (!hasMaterialCostItems()) {
                return false;
            }

            let unavailableProductsFound = false;

            for (const tier of buildEstimateTiers.value) {
                const tierId = tier.id;

                const allProductIds = tier.materialCosts
                    ?.filter(item => !item.isCategory && getProductIdFromItem(item))
                    .map(item => getProductIdFromItem(item)) || [];

                if (allProductIds.length === 0) {
                    continue;
                }

                if (!branchChangeState.value.productsResponseCache[tierId]) {
                    branchChangeState.value.productsResponseCache[tierId] = await productCatalogService.getProductsByIds(
                        newBranchCode,
                        allProductIds,
                        null,
                        true
                    );
                }

                const response = branchChangeState.value.productsResponseCache[tierId];

                if (response.success) {
                    let unavailableProductsIds = [];

                    if (response.data !== null) {
                        allProductIds.forEach(productId => {
                            const product = response.data.find(
                                p => (p.productId ?? p.ProductId) === productId
                            );

                            if (product) {
                                const variants = product.Variants ?? product.variants;
                                const allVariantsUnavailable = variants?.every(
                                    variant => !variant.AvailableAtBranch && !variant.availableAtBranch
                                );

                                if (allVariantsUnavailable) {
                                    unavailableProductsIds.push(productId);
                                }
                            } else {
                                unavailableProductsIds.push(productId);
                            }
                        });
                    } else {
                        unavailableProductsIds = [...allProductIds];
                    }

                    if (unavailableProductsIds.length > 0) {
                        const unavailableMaterials = tier.materialCosts.filter(itemSource => {
                            const productId = getProductIdFromItem(itemSource);
                            return productId && unavailableProductsIds.includes(productId);
                        });

                        const unavailableCustomMaterials = tier.customMaterialCosts?.filter(itemSource => {
                            const productId = getProductIdFromItem(itemSource);
                            return productId && unavailableProductsIds.includes(productId);
                        }) || [];

                        branchChangeState.value.unavailableProducts[tierId] = [
                            ...unavailableMaterials,
                            ...unavailableCustomMaterials
                        ];

                        unavailableProductsFound = true;
                    }
                }
            }

            return unavailableProductsFound;
        } catch (error) {
            console.error('Error checking branch change availability:', error);
            return false;
        } finally {
            branchChangeState.value.isChecking = false;
        }
    };

    const updateTierAvailabilityForBranch = async (newBranchCode) => {
        try {
            if (!hasMaterialCostItems()) {
                return;
            }

            for (const tier of buildEstimateTiers.value) {
                const tierId = tier.id;

                const allProductIds = tier.materialCosts
                    ?.filter(item => !item.isCategory && getProductIdFromItem(item))
                    .map(item => getProductIdFromItem(item)) || [];

                if (allProductIds.length === 0) {
                    continue;
                }

                if (!branchChangeState.value.productsResponseCache[tierId]) {
                    branchChangeState.value.productsResponseCache[tierId] = await productCatalogService.getProductsByIds(
                        newBranchCode,
                        allProductIds,
                        null,
                        true
                    );
                }

                const response = branchChangeState.value.productsResponseCache[tierId];

                if (response.success) {
                    if (response.data !== null) {
                        tier.materialCosts
                            .filter(item => !item.isCategory && getProductIdFromItem(item))
                            .forEach(tierProduct => {
                                const productId = getProductIdFromItem(tierProduct);

                                const product = response.data.find(
                                    p => (p.productId ?? p.ProductId) === productId
                                );

                                const catalogVariants = tierProduct.catalogInfo?.variants || tierProduct.catalogInfo?.Variants;
                                const itemVariants = tierProduct.item?.Variants || tierProduct.Variants;

                                if (catalogVariants) {
                                    catalogVariants.forEach(tierVariant => {
                                        const selectableFeatures = product?.selectableFeatures ?? product?.SelectableFeatures;
                                        let variant;

                                        if (selectableFeatures?.includes('COLOR') || selectableFeatures?.includes('SIZE')) {
                                            variant = (product?.Variants ?? product?.variants)?.find(
                                                v => (v.ColorName ?? v.colorName) === (tierVariant.ColorName ?? tierVariant.colorName)
                                            );
                                        } else {
                                            variant = (product?.Variants ?? product?.variants)?.[0];
                                        }

                                        if (variant) {
                                            tierVariant.availableAtBranch = variant.availableAtBranch ?? variant.AvailableAtBranch;
                                            tierVariant.AvailableAtBranch = variant.availableAtBranch ?? variant.AvailableAtBranch;
                                        } else {
                                            tierVariant.availableAtBranch = false;
                                            tierVariant.AvailableAtBranch = false;
                                        }
                                    });
                                }

                                if (itemVariants) {
                                    itemVariants.forEach(tierVariant => {
                                        const selectableFeatures = product?.selectableFeatures ?? product?.SelectableFeatures;
                                        let variant;

                                        if (selectableFeatures?.includes('COLOR') || selectableFeatures?.includes('SIZE')) {
                                            variant = (product?.Variants ?? product?.variants)?.find(
                                                v => (v.ColorName ?? v.colorName) === (tierVariant.ColorName ?? tierVariant.colorName)
                                            );
                                        } else {
                                            variant = (product?.Variants ?? product?.variants)?.[0];
                                        }

                                        if (variant) {
                                            tierVariant.AvailableAtBranch = variant.availableAtBranch ?? variant.AvailableAtBranch;
                                        } else {
                                            tierVariant.AvailableAtBranch = false;
                                        }
                                    });
                                }
                            });
                    } else {
                        tier.materialCosts
                            .filter(item => !item.isCategory && getProductIdFromItem(item))
                            .forEach(tierProduct => {
                                const catalogVariants = tierProduct.catalogInfo?.variants || tierProduct.catalogInfo?.Variants;
                                if (catalogVariants) {
                                    catalogVariants.forEach(tierVariant => {
                                        tierVariant.availableAtBranch = false;
                                        tierVariant.AvailableAtBranch = false;
                                    });
                                }

                                const itemVariants = tierProduct.item?.Variants || tierProduct.Variants;
                                if (itemVariants) {
                                    itemVariants.forEach(tierVariant => {
                                        tierVariant.AvailableAtBranch = false;
                                    });
                                }
                            });
                    }
                }
            }
        } catch (error) {
            console.error('Error updating tier availability:', error);
        }
    };

    const resetBranchChangeState = () => {
        branchChangeState.value.pendingBranchCode = null;
        branchChangeState.value.unavailableProducts = {};
        branchChangeState.value.productsResponseCache = {};
    };

    const setPendingBranchChange = (newBranchCode, previousBranchCode) => {
        branchChangeState.value.pendingBranchCode = newBranchCode;
        branchChangeState.value.previousBranchCode = previousBranchCode;
    };

    // ===== JOB ACCOUNT METHODS =====

    const setJobAccounts = (accountList) => {
        jobAccounts.value = accountList || [];
    };

    const filterJobAccountsByBranch = (allAccounts, branchCode, branchShipToMap) => {
        if (!branchCode || !branchShipToMap) return [];

        const branchShipToList = branchShipToMap[branchCode];
        if (!branchShipToList) return [];

        return allAccounts.filter((acc) => branchShipToList.includes(acc.value));
    };

    // ===== ROOF PITCH METHODS =====
    const fetchRoofPitchValues = async () => {
        try {
            loadingRoofPitch.value = true;
            const response = await createEstimatorService.getRoofPitchValues();
            
            if (response.success && response.data) {
                roofPitchValues.value = response.data;
                return response.data;
            }
            
            return [];
        } catch (error) {
            console.error('Error fetching roof pitch values:', error);
            return [];
        } finally {
            loadingRoofPitch.value = false;
        }
    };

    const setRoofPitchValues = (pitchList) => {
        roofPitchValues.value = pitchList || [];
    };

    // ===== ESTIMATOR SETTINGS =====
    // estimatorSettings is now in estimatorData
    
    const fetchEstimatorSettings = async () => {
        try {
            const response = await createEstimatorService.getEstimatorSettings();
            
            if (response.success && response.data) {
                estimatorSettings.value = response.data;
                
                // Apply settings to all tiers 
                const settings = response.data.EstimateCompanySetting;
                if (settings) {
                    applySettingsToTiers(settings);
                }
                
                // Load additional settings 
                await loadEstimateSettings(response.data);
                
                return response.data;
            }
            
            return null;
        } catch (error) {
            console.error('Error fetching estimator settings:', error);
            return null;
        }
    };

    // Load additional estimator settings 
    const loadEstimateSettings = async (responseData) => {
        if (!responseData) return;
        
        try {
            // Update tier titles from TierSettings 
            if (responseData.EstimateCompanyTierSettings && Array.isArray(responseData.EstimateCompanyTierSettings)) {
                buildEstimateTiers.value.forEach(tier => {
                    const tierSetting = responseData.EstimateCompanyTierSettings.find(i => i.TierID === tier.id);
                    if (tierSetting && tierSetting.Label) {
                        tier.title = tierSetting.Label;
                    }
                });
            }
            
            // Load testimonials 
            if (responseData.testimonials) {
                estimatorData.value.buildProposalData.testimonials.isSelected = true;
                estimatorData.value.buildProposalData.testimonials.content = responseData.testimonials;
            }
            
            // Note: Cover Page images, Company Logo images, AboutUs, TermsAndConditions, and ScopeOfWork
            // will be handled in the Build Proposal step when needed
        } catch (error) {
            console.error('Error loading estimate settings:', error);
        }
    };

    // ===== TEMPLATES =====
    // Template state is now in estimatorData.templates

    const flattenTemplatesFromCategories = (categories, tierKey = null) => {
        if (!Array.isArray(categories)) return [];
        return categories.reduce((acc, category) => {
            // Get category name (API returns CategoryName)
            const categoryName = category.CategoryName || category.Name || category.name || '';
            
            // Add category header
            acc.push({
                id: null,
                name: categoryName.toUpperCase()
            });
            
            // Add templates from this category
            if (Array.isArray(category.Templates)) {
                // Sort templates alphabetically by name
                const sortedTemplates = [...category.Templates].sort((a, b) => {
                    const nameA = (a.Name || a.name || '').toLowerCase();
                    const nameB = (b.Name || b.name || '').toLowerCase();
                    return nameA.localeCompare(nameB);
                });
                
                sortedTemplates.forEach(template => {
                    // Check if template has costs (same logic as AssignTemplatesModal)
                    let hasCosts = template.HasCosts || template.hasCosts || false;
                    if (!hasCosts && template.TemplateOptions) {
                        try {
                            const templateOptions = typeof template.TemplateOptions === 'string' 
                                ? JSON.parse(template.TemplateOptions) 
                                : template.TemplateOptions;
                            hasCosts = (templateOptions?.labor?.length > 0) || 
                                       (templateOptions?.otherCharges?.length > 0);
                        } catch (e) {
                            // Ignore parse errors
                        }
                    }
                    
                    acc.push({
                        id: template.TemplateId || template.Id || template.id,
                        name: template.Name || template.name || '',
                        categoryName: categoryName.toUpperCase(),
                        hasCosts: hasCosts,
                        tier: template.Tier || template.tier || tierKey
                    });
                });
            }
            return acc;
        }, []);
    };

    const fetchTemplates = async (branchCode, forceRefresh = false) => {
        if (!branchCode) return false;
        
        // Skip fetch if templates are already loaded for this branch (unless forced)
        if (!forceRefresh && templatesBranchCode.value === branchCode && allTemplates.value.length > 0) {
            return true;
        }
        
        // Skip if already loading
        if (loadingTemplates.value) {
            return false;
        }
        
        try {
            loadingTemplates.value = true;
            const result = await customerService.getTemplatesData(branchCode);
            
            if (result && result.categories) {
                templateCategories.value = result.categories;
                templatesBranchCode.value = branchCode; // Track which branch templates are for
                
                // Flatten all templates from all categories
                const allFlattenedTemplates = flattenTemplatesFromCategories(result.categories);
                
                // ALL tiers get the SAME template list (like AssignTemplatesModal)
                // User can select any template for any tier
                tierTemplates.value.good = [...allFlattenedTemplates];
                tierTemplates.value.better = [...allFlattenedTemplates];
                tierTemplates.value.best = [...allFlattenedTemplates];
                
                // Keep allTemplates for reference
                allTemplates.value = allFlattenedTemplates;
                
                return true;
            }
            
            return false;
        } catch (error) {
            console.error('Error fetching templates:', error);
            templateCategories.value = [];
            allTemplates.value = [];
            tierTemplates.value = { good: [], better: [], best: [] };
            return false;
        } finally {
            loadingTemplates.value = false;
        }
    };

    const setTemplates = (templates) => {
        allTemplates.value = templates || [];
    };

    const setTemplateCategories = (categories) => {
        templateCategories.value = categories || [];
        allTemplates.value = flattenTemplatesFromCategories(categories);
    };

    // Get template info by ID from allTemplates
    const getTemplateById = (templateId) => {
        if (!templateId) return null;
        return allTemplates.value.find(t => t.id && String(t.id) === String(templateId));
    };

    // Set selected template for a specific tier
    const setSelectedTierTemplate = (tierKey, templateId, templateName = null) => {
        if (!tierKey) return;
        
        // If templateName not provided, try to find it
        let name = templateName;
        if (!name && templateId) {
            const template = getTemplateById(templateId);
            name = template?.name || '';
        }
        
        selectedTierTemplates.value[tierKey] = templateId ? { id: templateId, name } : null;
    };

    // Clear all selected tier templates
    const clearSelectedTierTemplates = () => {
        selectedTierTemplates.value = { good: null, better: null, best: null };
    };

    // Load template details by ID and populate material costs (OLD Material Cost flow)
    // Flow: 1) Get template Items, 2) Extract ProductIds, 3) Fetch full product details, 4) Adapt items
    const loadingTemplateDetails = ref(false);
    const selectedTemplateId = ref(null);
    // Note: selectedTemplateTier is now defined near activeTierId above

    // Siding custom measurement rules are owned by the template, never copied onto the estimate.
    // Cached per templateId as a map of templateItemId -> active siding measurement keys, so
    // callers can scope the result to the items actually present in the estimate.
    const templateSidingMeasurementKeysCache = ref({});
    const loadingTemplateSidingMeasurementKeys = ref(false);

    const extractActiveSidingMeasurementKeysByItem = (templateData) => {
        const byProduct = {};

        (templateData?.Items || []).forEach(item => {
            let itemOptions = item?.ItemOptions;
            if (typeof itemOptions === 'string') {
                try {
                    itemOptions = JSON.parse(itemOptions);
                } catch (e) {
                    return;
                }
            }  

            // Template ItemId is not reliably populated in the details response, so key by
            // ProductId, which is stable and shared with the estimate rows.
            const productId = itemOptions?.ProductId ?? itemOptions?.Options?.ProductId;
            if (!productId) return;

            // Rules are stored inside the nested Options bag; tolerate a flat shape too.
            const sidingRules = itemOptions?.Options?.CustomMeasurementsSiding
                || itemOptions?.CustomMeasurementsSiding;
            if (!sidingRules) return;

            const activeKeys = Object.entries(sidingRules)
                .filter(([, rule]) => rule?.Active === true)
                .map(([key]) => key);

            if (activeKeys.length > 0) {
                byProduct[String(productId)] = activeKeys;
            }
        });

        return byProduct;
    };

    // Returns the siding measurement identifiers that have an ACTIVE custom rule on the template,
    // scoped to the products actually present in the estimate.
    // productIds = null means "no scoping" (union across the whole template).
    const loadTemplateSidingMeasurementKeys = async (templateId, productIds = null) => {
        if (!templateId || templateId === '0' || templateId === 0) return [];

        const cacheKey = String(templateId);

        const scopeKeys = (byProduct) => {
            const scoped = new Set();
            const wanted = productIds === null
                ? null
                : new Set(Array.from(productIds).map(String));

            Object.entries(byProduct || {}).forEach(([productId, keys]) => {
                if (wanted && !wanted.has(productId)) return;
                keys.forEach(key => scoped.add(key));
            });

            return Array.from(scoped);
        };

        const cached = templateSidingMeasurementKeysCache.value[cacheKey];
        if (cached) return scopeKeys(cached);

        try {
            loadingTemplateSidingMeasurementKeys.value = true;

            const response = await orderTemplateService.getOrderTemplateDetailsById(templateId);
            if (!response.success || !response.data) return [];

            const templateData = response.data.Data || response.data;
            const byProduct = extractActiveSidingMeasurementKeysByItem(templateData);

            // Only cache a meaningful result; caching {} would make a transient miss permanent.
            if (Object.keys(byProduct).length > 0) {
                templateSidingMeasurementKeysCache.value = {
                    ...templateSidingMeasurementKeysCache.value,
                    [cacheKey]: byProduct
                };
            }

            return scopeKeys(byProduct);
        } catch (error) {
            console.error('[createEstimateStore] Error loading template siding measurement keys:', error);
            return [];
        } finally {
            loadingTemplateSidingMeasurementKeys.value = false;
        }
    };

    const clearTemplateSidingMeasurementKeys = (templateId = null) => {
        if (templateId === null) {
            templateSidingMeasurementKeysCache.value = {};
            return;
        }

        const next = { ...templateSidingMeasurementKeysCache.value };
        delete next[String(templateId)];
        templateSidingMeasurementKeysCache.value = next;
    };

    // Helper function to adapt product catalog data with template items (like MCM_getProductsByIdsAdaptItems)
    const adaptProductsWithTemplateItems = (products, templateItems) => {
        return templateItems.map(function (tempItem) {
            try {
                var itemOptions = JSON.parse(tempItem.ItemOptions);
                let item = products.find(function (x) {
                    let itemProductId = x.productId !== undefined ? x.productId : x.ProductId;
                    return itemOptions.ProductId === itemProductId;
                });

                if (!item) {
                    console.warn('[createEstimateStore] Product not found for:', itemOptions.ProductId);
                    return null;
                }

                var Variants = [];
                let itemVariants = item.variants !== undefined ? item.variants : item.Variants;

                for (var j = 0; j < (itemVariants || []).length; j++) {
                    var variant = itemVariants[j];
                    // CRITICAL: Preserve actual branch availability - don't default to true
                    // The API returns accurate availability based on branch code in getProductsByIds
                    var variantAvailability = variant.availableAtBranch !== undefined ? variant.availableAtBranch : 
                                             (variant.AvailableAtBranch !== undefined ? variant.AvailableAtBranch : false);
                    
                    Variants.push({
                        AgilityDescription: variant.agilityDescription !== undefined ? variant.agilityDescription : variant.AgilityDescription,
                        AgilitySize: variant.agilitySize !== undefined ? variant.agilitySize : variant.AgilitySize,
                        AvailableAtBranch: variantAvailability,
                        ColorName: variant.colorName !== undefined ? variant.colorName : variant.ColorName,
                        CustomerRestrictions: variant.customerRestrictions !== undefined ? variant.customerRestrictions : variant.CustomerRestrictions,
                        Description: variant.description !== undefined ? variant.description : variant.Description,
                        ImageUrl: (variant.mediaAssets && variant.mediaAssets.length) > 0 ? variant.mediaAssets[0].assetUri : variant.ImageUrl,
                        ItemCode: variant.itemCode !== undefined ? variant.itemCode : variant.ItemCode,
                        ItemId: variant.itemId !== undefined ? variant.itemId : variant.ItemId,
                        ItemWebName: variant.itemWebName !== undefined ? variant.itemWebName : variant.ItemWebName,
                        MajorCode: variant.majorCode !== undefined ? variant.majorCode : variant.MajorCode,
                        MarketingDescription: variant.marketingDescription !== undefined ? variant.marketingDescription : variant.MarketingDescription,
                        MinorCode: variant.minorCode !== undefined ? variant.minorCode : variant.MinorCode,
                        ModifiedDate: variant.modifiedDate !== undefined ? variant.modifiedDate : variant.ModifiedDate,
                        MediaAssets: variant.mediaAssets !== undefined ? variant.mediaAssets : variant.MediaAssets,
                        OnHand: variant.onHand !== undefined ? variant.onHand : variant.OnHand,
                        RoofhubItemCode: variant.roofhubItemCode !== undefined ? variant.roofhubItemCode : variant.RoofhubItemCode,
                        Segment: variant.segment !== undefined ? variant.segment : variant.Segment,
                        SelectableFeatures: variant.selectableFeatures !== undefined ? variant.selectableFeatures : variant.SelectableFeatures,
                        Series: variant.series !== undefined ? variant.series : variant.Series,
                        SizeName: variant.sizeName !== undefined ? variant.sizeName : variant.SizeName,
                        Specifications: variant.specifications !== undefined ? variant.specifications : variant.Specifications,
                        UOMs: variant.uoms !== undefined ? variant.uoms.map((u) => ({
                            ConversionCategory: u.conversionCategory,
                            IsDefaultMeasure: u.isDefaultMeasure,
                            MeasurementConversionFactor: u.measurementConversionFactor,
                            Name: u.name
                        })) : variant.UOMs,
                        Warranty: variant.warranty !== undefined ? variant.warranty : variant.Warranty
                    });
                }

                // Set default variant behavior (matching browse catalog flow)
                // Priority: 1) Single variant products auto-select
                //          2) Template saved variant ONLY if available at branch
                //          3) Otherwise leave unselected (user must choose)
                var SelectedVariant = null;
                
                // If only one variant, auto-select it (like browse catalog)
                if (Variants.length === 1) {
                    SelectedVariant = Variants[0];
                } else {
                    // Multiple variants: try to use template's saved selection ONLY if available
                    for (var k = 0; k < Variants.length; k++) {
                        if (Variants[k].ItemCode === itemOptions.ItemCode) {
                            // Found template's saved variant - only use if available at current branch
                            if (Variants[k].AvailableAtBranch) {
                                SelectedVariant = Variants[k];
                            }
                            break;
                        }
                    }
                    // If template variant not found or not available, leave null (user must select)
                }

                var SelectedUoms = null;

                if (SelectedVariant) {
                    for (var l = 0; l < SelectedVariant.UOMs.length; l++) {
                        if (SelectedVariant.UOMs[l].Name === itemOptions.DefaultUOM) {
                            SelectedUoms = SelectedVariant.UOMs[l];
                            break;
                        }
                    }
                }

                return {
                    Category: item.category !== undefined ? item.category : item.Category,
                    CategoryRank: 0,
                    DataSheetUrl: item.dataSheetUrl !== undefined ? item.dataSheetUrl : item.DataSheetUrl,
                    FeatureBullets: item.featureBullets !== undefined ? item.featureBullets : [],
                    ImageUrl: item.imageUrl !== undefined ? item.imageUrl : item.ImageUrl,
                    IsGeneric: item.isGeneric !== undefined ? item.isGeneric : false,
                    Manufacturer: item.manufacturer !== undefined ? item.manufacturer : item.Manufacturer,
                    ProductId: item.productId !== undefined ? item.productId : item.ProductId,
                    ProductName: item.productName !== undefined ? item.productName : item.ProductName,
                    Promo: item.promo !== undefined ? item.promo : false,
                    Promotions: item.promotions !== undefined ? item.promotions : [],
                    Quantity: tempItem.Quantity,
                    RoofhubItemCode: item.roofhubItemCode !== undefined ? item.roofhubItemCode : item.RoofHubItemCode,
                    SelectableFeatures: item.selectableFeatures !== undefined ? item.selectableFeatures : item.SelectableFeatures,
                    SelectedUoms: SelectedUoms,
                    SelectedVariant: SelectedVariant,
                    Specification: item.specification !== undefined ? item.specification : null,
                    UnitPrice: 0,
                    Variants: Variants,
                    WebDescription: item.webDescription !== undefined ? item.webDescription : item.WebDescription,
                    WebName: item.webName !== undefined ? item.webName : item.WebName,
                    IsAvailableAtBranch: Variants.some(function(v) { 
                        return v.AvailableAtBranch === true || v.availableAtBranch === true; 
                    }),
                    Categories: item.categories !== undefined ? item.categories : item.Categories,
                    TemplateItemId: tempItem.ItemId
                };
            } catch (error) {
                console.error('[createEstimateStore] Error adapting item:', error);
                return null;
            }
        }).filter(function (item) {
            return item !== null;
        });
    };

    /**
     * Helper: Normalize variant properties to include both uppercase and lowercase keys
     * This ensures compatibility across different parts of the application
     * @param {Object} v - Variant object from API
     * @returns {Object} Normalized variant with both naming conventions
     */
    const normalizeVariant = (v) => {
        const isAvailable = v.availableAtBranch !== undefined 
            ? v.availableAtBranch 
            : (v.AvailableAtBranch !== undefined ? v.AvailableAtBranch : false);
        
        const normalizeUom = (u) => ({
            name: u.name || u.Name,
            conversionCategory: u.conversionCategory || u.ConversionCategory,
            isDefaultMeasure: u.isDefaultMeasure !== undefined ? u.isDefaultMeasure : u.IsDefaultMeasure,
            measurementConversionFactor: u.measurementConversionFactor || u.MeasurementConversionFactor
        });
        
        const uoms = (v.uoms || v.UOMs || []).map(normalizeUom);
        
        return {
            // Lowercase (for component)
            colorName: v.colorName || v.ColorName || null,
            sizeName: v.sizeName || v.SizeName || null,
            itemCode: v.itemCode || v.ItemCode || null,
            availableAtBranch: isAvailable,
            imageUrl: v.imageUrl || v.ImageUrl || (v.mediaAssets?.[0]?.assetUri) || '',
            agilitySize: v.agilitySize || v.AgilitySize || null,
            uoms,
            mediaAssets: v.mediaAssets || v.MediaAssets || [],
            // Uppercase (for compatibility)
            ColorName: v.colorName || v.ColorName || null,
            SizeName: v.sizeName || v.SizeName || null,
            ItemCode: v.itemCode || v.ItemCode || null,
            AvailableAtBranch: isAvailable,
            ImageUrl: v.imageUrl || v.ImageUrl || (v.mediaAssets?.[0]?.assetUri) || '',
            AgilitySize: v.agilitySize || v.AgilitySize || null,
            UOMs: uoms.map(u => ({
                Name: u.name,
                ConversionCategory: u.conversionCategory,
                IsDefaultMeasure: u.isDefaultMeasure,
                MeasurementConversionFactor: u.measurementConversionFactor
            }))
        };
    };

    // Transform adapted products to MaterialCostTable row format
    // Legacy code uses adapted products directly, but Vue needs wrapper structure for MaterialItemRow component
    const transformToMaterialCostRows = function (adaptedProducts) {
        return adaptedProducts.map(function (product, idx) {
            var selectedVariant = product.SelectedVariant;
            // CRITICAL: Only use color/size if selectedVariant exists AND is available
            // If variant was cleared due to branch availability, don't set color/size
            var selectedColor = (selectedVariant && selectedVariant.AvailableAtBranch) ? selectedVariant.ColorName : null;
            var selectedSize = (selectedVariant && selectedVariant.AvailableAtBranch) ? (selectedVariant.AgilitySize || selectedVariant.SizeName) : null;
            var defaultUOM = (product.SelectedUoms && selectedVariant && selectedVariant.AvailableAtBranch) ? product.SelectedUoms.Name : null;
            
            // Normalize variants using helper function (eliminates duplicate code)
            var normalizedVariants = (product.Variants || []).map(normalizeVariant);
            
            return {
                id: 'product-' + product.ProductId + '-' + Date.now() + '-' + idx,
                isCategory: false,
                added: false,
                pricing: {},
                category: product.Category || '',
                catalogInfo: {
                    productId: product.ProductId,
                    ProductId: product.ProductId,
                    productName: product.ProductName,
                    ProductName: product.ProductName,
                    manufacturer: product.Manufacturer,
                    Manufacturer: product.Manufacturer,
                    categories: product.Categories,
                    Categories: product.Categories,
                    // MaterialItemRow expects lowercase 'variants' property
                    variants: normalizedVariants,
                    Variants: normalizedVariants,
                    selectableFeatures: product.SelectableFeatures,
                    SelectableFeatures: product.SelectableFeatures
                },
                item: {
                    ProductId: product.ProductId,
                    ProductName: product.ProductName || product.WebName,
                    Manufacturer: product.Manufacturer,
                    ImageUrl: product.ImageUrl || (selectedVariant ? selectedVariant.ImageUrl : '') || '',
                    Quantity: product.Quantity || 0,
                    UnitPrice: product.UnitPrice || 0,
                    WasteFactor: product.ItemOptions?.WasteFactor || 0,
                    MeasurementFactor: product.ItemOptions?.MeasurementFactor || 1,
                    Options: {
                        color: selectedColor,
                        size: selectedSize
                    },
                    DefaultUOM: defaultUOM,
                    SelectedColor: selectedColor,
                    SelectedSize: selectedSize,
                    ItemOptions: product.ItemOptions,
                    Variants: normalizedVariants,
                    SelectedVariant: selectedVariant,
                    SelectedUoms: product.SelectedUoms
                }
            };
        });
    };

    const loadTemplateDetails = async (templateId, tier = null, isInitialLoad = false) => {
        if (!templateId) return { success: false, error: 'No template ID provided' };
        
        try {
            loadingTemplateDetails.value = true;
            selectedTemplateId.value = templateId;
            
            // Determine target tier ID: use provided tier, otherwise use active tier
            const targetTierId = tier !== null ? tier : activeTierId.value;
            
            // Update selectedTemplateTier if tier is explicitly provided
            if (tier !== null) {
                selectedTemplateTier.value = tier;
            }
            
            // Step 1: Get template data
            const response = await orderTemplateService.getOrderTemplateDetailsById(templateId);
            
            if (!response.success || !response.data) {
                return { success: false, error: 'Failed to load template' };
            }
            
            const templateData = response.data.Data || response.data;

            // Hydrate selected template metadata immediately so Step 2 label is available
            // even when template has no items or no valid product IDs.
            const numericTierId = Number(targetTierId);
            const tierKey = numericTierId === 1 ? 'good' : (numericTierId === 2 ? 'better' : 'best');
            const templateName = templateData.TemplateName || templateData.Name || '';
            setSelectedTierTemplate(tierKey, templateId, templateName);
            setTierTemplateId(numericTierId, String(templateId));
            
            // Step 2: For tier 1 AND initial load only, extract and set branch and job account from template options
            // This ensures the estimate uses the template's branch for product fetching
            // Skip this when switching templates to preserve user's branch/job account selection
            if (targetTierId === 1 && isInitialLoad && templateData.TemplateOptions) {
                try {
                    const templateOptions = typeof templateData.TemplateOptions === 'string'
                        ? JSON.parse(templateData.TemplateOptions)
                        : templateData.TemplateOptions;
                    
                    const branchCode = templateOptions.BranchFilter;
                    const jobAccountId = templateOptions.ShipToSequenceId;
                    
                    // Update branch if available
                    if (branchCode) {
                        const branches = estimatorData.value.jobInformation.branchList;
                        const branch = branches.find(b => b.BranchCode === branchCode);
                        if (branch) {
                            estimatorData.value.jobInformation.selectedBranch = branch.BranchCode;
                            // Branch is set from template, not from closest branch logic
                            isClosestBranchSelected.value = false;
                        }
                    }
                    
                    // Update job account if available
                    if (jobAccountId) {
                        estimatorData.value.jobInformation.jobAccount = jobAccountId;
                    }
                } catch (parseError) {
                    console.error('[createEstimateStore] Error parsing template options:', parseError);
                }
            }
            
            // Step 3: Extract template Items and parse ItemOptions (like legacy)
            const templateItems = (templateData.Items || [])
                .map(item => {
                    try {
                        const itemOptions = typeof item.ItemOptions === 'string' 
                            ? JSON.parse(item.ItemOptions) 
                            : item.ItemOptions || {};
                        return { ...item, ItemOptions: itemOptions };
                    } catch (e) {
                        console.error('[createEstimateStore] Error parsing ItemOptions:', e);
                        return null;
                    }
                })
                .filter(item => item && item.ItemOptions?.ProductId)
                // Keep exact template-defined product sequence (same pattern as Order Wizard).
                .sort((a, b) => {
                    const aOrder = a.UserSort ?? a.SortOrder ?? a.DisplayOrder ?? a.ItemOrder ?? a.ItemId ?? 0;
                    const bOrder = b.UserSort ?? b.SortOrder ?? b.DisplayOrder ?? b.ItemOrder ?? b.ItemId ?? 0;
                    return aOrder - bOrder;
                });
            
            if (templateItems.length === 0) {
                setMaterialCosts([]);
                return { success: true, data: templateData };
            }
            
            // Step 4: Get ProductIds from template items (filter out null/undefined ProductIds)
            const productIds = templateItems
                .map(item => item.ItemOptions.ProductId)
                .filter(id => id); // Remove null/undefined values to prevent "Unknown Product" entries
            
            if (productIds.length === 0) {
                console.warn('[createEstimateStore] No valid ProductIds found in template items');
                setMaterialCosts([]);
                return { success: true, data: templateData };
            }
            
            // Step 5: Batch fetch products using byIds API for template/material bulk loading
            const branchCode = estimatorData.value.jobInformation.selectedBranch || '';
            const uniqueProductIds = [...new Set(productIds)];

            let products = [];
            const byIdsResponse = await productCatalogService.getProductsByIds(branchCode, uniqueProductIds);

            if (byIdsResponse?.success && Array.isArray(byIdsResponse.data) && byIdsResponse.data.length > 0) {
                products = byIdsResponse.data;
            } else {
                // Fallback to details API if byIds fails so template loading still works
                const productPromises = uniqueProductIds.map(async (productId) => {
                    const detailsPayload = {
                        BranchCode: branchCode,
                        ExcludeProductsNotAvailable: false,
                        ProductId: productId
                    };
                    const response = await productCatalogService.getProductDetailsById(detailsPayload);
                    if (response?.success && response?.data?.Data) {
                        return response.data.Data;
                    }
                    return null;
                });

                const productResults = await Promise.all(productPromises);
                products = productResults.filter(p => p !== null);
            }
            
            if (products.length === 0) {
                console.error('[createEstimateStore] Failed to fetch products');
                return { success: false, error: 'Failed to fetch product details' };
            }
            
            // Step 6: Transform template items to material cost rows (matching legacy flow)
            const materialCostRows = templateItems.map((templateItem, index) => {
                const product = products.find(p => 
                    (p.productId || p.ProductId) === templateItem.ItemOptions.ProductId
                );
                
                // Skip products not available at this branch
                if (!product) {
                    return null;
                }
                
                // Normalize variants using helper function
                const variants = (product.variants || product.Variants || []).map(normalizeVariant);
                
                // Find selected variant by ItemCode (from template)
                let selectedVariant = templateItem.ItemOptions.ItemCode 
                    ? variants.find(v => v.itemCode === templateItem.ItemOptions.ItemCode)
                    : null;
                
                // Auto-select if only one variant (like legacy)
                if (!selectedVariant && variants.length === 1) {
                    selectedVariant = variants[0];
                }
                
                // Extract selection details
                const selectedColor = selectedVariant?.colorName || '';
                const selectedSize = selectedVariant?.sizeName || selectedVariant?.agilitySize || '';
                const imageUrl = selectedVariant?.imageUrl || product.imageUrl || product.ImageUrl || '';
                const defaultUom = selectedVariant?.uoms.find(u => u.isDefaultMeasure) || selectedVariant?.uoms[0];
                const defaultUOM = templateItem.ItemOptions.DefaultUOM || defaultUom?.name || 'EA';
                
                const productId = product.productId || product.ProductId;
                const productName = product.productName || product.ProductName;
                const manufacturer = product.manufacturer || product.Manufacturer;
                
                // Create material cost row (matching legacy structure)
                return {
                    id: `product-${productId}-${Date.now()}-${index}`,
                    isCategory: false,
                    added: false,
                    pricing: {},
                    category: product.categories?.[0]?.categoryName || templateItem.Category || '',
                    catalogInfo: {
                        productId,
                        ProductId: productId,
                        productName,
                        ProductName: productName,
                        manufacturer,
                        Manufacturer: manufacturer,
                        categories: product.categories || product.Categories,
                        Categories: product.categories || product.Categories,
                        variants,
                        Variants: variants,
                        selectableFeatures: product.selectableFeatures || product.SelectableFeatures || [],
                        SelectableFeatures: product.selectableFeatures || product.SelectableFeatures || [],
                        webName: product.webName || productName,
                        itemCode: product.itemCode || product.ItemCode
                    },
                    item: {
                        ProductId: productId,
                        ProductName: productName,
                        Manufacturer: manufacturer,
                        ImageUrl: imageUrl,
                        Quantity: templateItem.Quantity || 0,
                        UnitPrice: 0,
                        WasteFactor: templateItem.ItemOptions?.WasteFactor || 0,
                        MeasurementFactor: templateItem.ItemOptions?.MeasurementFactor || 1,
                        Options: { color: selectedColor, size: selectedSize },
                        DefaultUOM: defaultUOM,
                        SelectedColor: selectedColor,
                        SelectedSize: selectedSize,
                        TemplateSelectedSize: selectedSize,
                        Variants: variants,
                        SelectedVariant: selectedVariant,
                        selectedVariant: selectedVariant,
                        SelectedUoms: selectedVariant ? selectedVariant.uoms.find(u => u.name === defaultUOM) || selectedVariant.uoms[0] : null,
                        FromTemplateId: templateData.TemplateId,
                        TemplateItemId: templateItem.ItemId
                    }
                };
            }).filter(item => item !== null);
            
            // Step 7: Clear ALL existing products and data for this tier before adding new template products
            const targetTier = getTierById(targetTierId);
            
            if (targetTier) {
                // Clear material costs (regular products)
                targetTier.materialCosts = [];
                // Clear custom material costs
                targetTier.customMaterialCosts = [];
                // Clear any validation errors
                targetTier.materialItemsWithProblems = [];
            }
            
            // Step 8: Set new material costs from template directly on target tier (not active tier)
            if (targetTier) {
                targetTier.materialCosts = materialCostRows;
            }
            // Also update flat ref for backward compatibility if this is the active tier
            if (targetTierId === activeTierId.value) {
                materialCosts.value = materialCostRows;
            }
            
            // Step 9: Clear ALL BOM items for this tier, then add new template items
            const globalCatalogStore = useGlobalCatalogStore();
            
            // CRITICAL: Remove ALL existing items for this tier from BOM immediately
            // Use array reassignment to ensure Vue reactivity triggers immediately
            const filteredBOM = globalCatalogStore.externalCartForBOM.filter(
                item => item.tierId !== targetTierId
            );
            globalCatalogStore.externalCartForBOM.splice(0, globalCatalogStore.externalCartForBOM.length, ...filteredBOM);
            
            // Now add new template items to BOM (iterate over original materialCostRows, not tier.materialCosts)
            materialCostRows.forEach((row) => {
                // Safety checks - skip invalid rows
                if (!row?.id || !row?.item?.SelectedVariant || !row?.catalogInfo) {
                    return;
                }
                
                const itemCode = row.item.SelectedVariant.itemCode || row.item.SelectedVariant.ItemCode;
                if (!itemCode) {
                    return;
                }
                
                // Create BOM item with all required fields explicitly set
                const bomItem = {
                    id: row.id,
                    itemCode: itemCode,
                    ItemCode: itemCode, // Add uppercase for compatibility
                    tierId: targetTierId,
                    addedFromBOM: true,
                    addedFromTemplate: true,
                    branchCode: branchCode,
                    // Catalog info fields
                    productId: row.catalogInfo.productId || row.catalogInfo.ProductId,
                    ProductId: row.catalogInfo.productId || row.catalogInfo.ProductId,
                    productName: row.catalogInfo.productName || row.catalogInfo.ProductName,
                    ProductName: row.catalogInfo.productName || row.catalogInfo.ProductName,
                    manufacturer: row.catalogInfo.manufacturer || row.catalogInfo.Manufacturer,
                    Manufacturer: row.catalogInfo.manufacturer || row.catalogInfo.Manufacturer,
                    categories: row.catalogInfo.categories || row.catalogInfo.Categories || [],
                    Categories: row.catalogInfo.categories || row.catalogInfo.Categories || [],
                    variants: row.catalogInfo.variants || row.catalogInfo.Variants || [],
                    Variants: row.catalogInfo.variants || row.catalogInfo.Variants || [],
                    selectableFeatures: row.catalogInfo.selectableFeatures || row.catalogInfo.SelectableFeatures || [],
                    SelectableFeatures: row.catalogInfo.selectableFeatures || row.catalogInfo.SelectableFeatures || [],
                    // Selection fields
                    SelectedColor: row.item.SelectedColor || '',
                    selectedVariant: row.item.selectedVariant || row.item.SelectedVariant,
                    SelectedVariant: row.item.SelectedVariant,
                    selectedSize: row.item.SelectedSize || ''
                };
                
                // Add to BOM
                globalCatalogStore.externalCartForBOM.push(bomItem);
            });
            
            // Step 9.5: Clean up any null/undefined entries in BOM (safety measure)
            globalCatalogStore.externalCartForBOM = globalCatalogStore.externalCartForBOM.filter(item => {
                // Remove invalid entries
                return item && item.id;
            });
            
            // Step 10: Reapply estimator settings
            if (estimatorSettings.value?.EstimateCompanySetting) {
                const settings = estimatorSettings.value.EstimateCompanySetting;
                if (targetTier?.calculateCosts) {
                    Object.assign(targetTier.calculateCosts, {
                        netProfitGoal: settings.NetProfitGoal || 0,
                        materialTaxRate: settings.TaxRate || 0,
                        overhead: settings.Overhead || 0
                    });
                }
            }
            
            // Step 11: Fetch pricing immediately after template loads (Issue 1 fix)
            await fetchTierPricing(targetTierId);
            
            return { success: true, data: templateData };
        } catch (error) {
            console.error('[createEstimateStore] Error loading template details:', error);
            return { success: false, error: error.message };
        } finally {
            loadingTemplateDetails.value = false;
        }
    };

    // ===== MATERIAL COST METHODS =====
    // Note: These methods now work with the ACTIVE TIER's materialCosts (matching old code behavior)
    const setMaterialCosts = (costs, branchCode = null, jobAccountId = null) => {
        // Update active tier's material costs (new tier-based behavior)
        const tier = activeTier.value;
        if (tier) {
            tier.materialCosts = costs || [];
        }
        // Also update flat ref for backward compatibility
        materialCosts.value = costs || [];
        // Track which branch/job account these materials are for
        if (branchCode !== null) {
            materialCostsBranchCode.value = branchCode;
        }
        if (jobAccountId !== null) {
            materialCostsJobAccountId.value = jobAccountId;
        }
    };

    const setCustomMaterialCosts = (costs) => {
        // Update active tier's custom material costs (new tier-based behavior)
        const tier = activeTier.value;
        if (tier) {
            tier.customMaterialCosts = costs || [];
        }
        // Also update flat ref for backward compatibility
        customMaterialCosts.value = costs || [];
    };

    const updateMaterialCostsBranchInfo = (branchCode, jobAccountId) => {
        materialCostsBranchCode.value = branchCode || '';
        materialCostsJobAccountId.value = jobAccountId || '';
    };

    const addMaterialCost = (item) => {
        // Add to active tier's material costs
        const tier = activeTier.value;
        if (tier) {
            tier.materialCosts.push(item);
        }
        // Also update flat ref for backward compatibility
        materialCosts.value.push(item);
    };

    const updateMaterialCost = (index, item) => {
        // Update in active tier's material costs
        const tier = activeTier.value;
        if (tier && index >= 0 && index < tier.materialCosts.length) {
            tier.materialCosts.splice(index, 1, item);
        }
        // Also update flat ref for backward compatibility
        if (index >= 0 && index < materialCosts.value.length) {
            materialCosts.value.splice(index, 1, item);
        }
    };

    const removeMaterialCost = (index) => {
        // Remove from active tier's material costs
        const tier = activeTier.value;
        if (tier && index >= 0 && index < tier.materialCosts.length) {
            tier.materialCosts.splice(index, 1);
        }
        // Also update flat ref for backward compatibility
        if (index >= 0 && index < materialCosts.value.length) {
            materialCosts.value.splice(index, 1);
        }
    };

    const addCustomMaterialCost = (item) => {
        // Add to active tier's custom material costs
        const tier = activeTier.value;
        if (tier) {
            tier.customMaterialCosts.push(item);
        }
        // Also update flat ref for backward compatibility
        customMaterialCosts.value.push(item);
    };

    const updateCustomMaterialCost = (index, item) => {
        // Update in active tier's custom material costs
        const tier = activeTier.value;
        if (tier && index >= 0 && index < tier.customMaterialCosts.length) {
            tier.customMaterialCosts.splice(index, 1, item);
        }
        // Also update flat ref for backward compatibility
        if (index >= 0 && index < customMaterialCosts.value.length) {
            customMaterialCosts.value.splice(index, 1, item);
        }
    };

    const removeCustomMaterialCost = (index) => {
        // Remove from active tier's custom material costs
        const tier = activeTier.value;
        if (tier && index >= 0 && index < tier.customMaterialCosts.length) {
            tier.customMaterialCosts.splice(index, 1);
        }
        // Also update flat ref for backward compatibility
        if (index >= 0 && index < customMaterialCosts.value.length) {
            customMaterialCosts.value.splice(index, 1);
        }
    };

    const clearMaterialCosts = () => {
        // Clear active tier's material costs
        const tier = activeTier.value;
        if (tier) {
            tier.materialCosts = [];
            tier.customMaterialCosts = [];
        }
        // Also clear flat refs for backward compatibility
        materialCosts.value = [];
        customMaterialCosts.value = [];
    };

    const setMaterialCostValidationErrors = (errors) => {
        materialCostValidationErrors.value = errors || [];
    };

    const clearMaterialCostValidationErrors = () => {
        materialCostValidationErrors.value = [];
    };

    // ===== BUILD PROPOSAL ACTIONS =====
    const updateAboutUs = (content) => {
        estimatorData.value.buildProposalData.aboutUs.content = content || '';
    };

    const updateTermsAndConditions = (content) => {
        estimatorData.value.buildProposalData.TermsAndConditions.content = content || '';
    };

    const updateCustomNotes = (content) => {
        estimatorData.value.buildProposalData.customNotes.content = content || '';
    };

    const getAboutUs = () => estimatorData.value.buildProposalData?.aboutUs?.content || '';
    const getTermsAndConditions = () => estimatorData.value.buildProposalData?.TermsAndConditions?.content || '';
    const getCustomNotes = () => estimatorData.value.buildProposalData?.customNotes?.content || '';

    // Property Report Actions
    const setPropertyReport = async (report) => {
        // Initialize propertyReport object if it doesn't exist
        if (!estimatorData.value.buildProposalData.propertyReport) {
            estimatorData.value.buildProposalData.propertyReport = {
                reportId: null,
                reportData: null,
                loading: false
            };
        }
        
        // Clear existing state first
        estimatorData.value.buildProposalData.propertyReport.reportId = null;
        estimatorData.value.buildProposalData.propertyReport.reportData = null;
        estimatorData.value.propertyReportId = '';
        estimatorData.value.buildProposalData.propertyReport.loading = false;
        
        // If no report provided, just clear and return
        if (!report) {
            return;
        }
        
        // Set report ID in both locations for API compatibility
        estimatorData.value.buildProposalData.propertyReport.reportId = report.ReportId;
        estimatorData.value.propertyReportId = report.ReportId;
        estimatorData.value.buildProposalData.propertyReport.loading = true;
        
        try {
            // Load full report details
            const propertyReportsService = (await import('@/services/propertyReportsService')).default;
            const response = await propertyReportsService.details(report.ReportId);
            
            if (response.success && response.data) {
                estimatorData.value.buildProposalData.propertyReport.reportData = response.data;
            }
        } catch (error) {
            console.error('Failed to load property report details:', error);
        } finally {
            estimatorData.value.buildProposalData.propertyReport.loading = false;
        }
    };

    const removePropertyReport = () => {
        // Initialize propertyReport object if it doesn't exist
        if (!estimatorData.value.buildProposalData.propertyReport) {
            estimatorData.value.buildProposalData.propertyReport = {
                reportId: null,
                reportData: null,
                loading: false
            };
        }
        
        estimatorData.value.buildProposalData.propertyReport.reportId = null;
        estimatorData.value.buildProposalData.propertyReport.reportData = null;
        estimatorData.value.propertyReportId = '';
    };

    const updatePropertyReportStatus = async (reportData) => {
        if (estimatorData.value.buildProposalData.propertyReport.reportId) {
            estimatorData.value.buildProposalData.propertyReport.reportData = reportData;
            estimatorData.value.buildProposalData.propertyReport.loading = false;
        }
    };

    const getPropertyReport = () => {
        const report = estimatorData.value.buildProposalData?.propertyReport;
        // Return null if report doesn't exist, is empty, or has no meaningful data
        if (!report || Object.keys(report).length === 0 || (!report.reportId && !report.reportData)) {
            return null;
        }
        return report;
    };

    /**
     * Helper function to set default content for build proposal sections
     * Only sets content if the section is currently empty (preserves edited content)
     * @param {string} section - The section key in buildProposalData
     * @param {string} defaultValue - The default value to set if section is empty
     */
    const setDefaultContent = (section, defaultValue) => {
        if (!estimatorData.value.buildProposalData[section]?.content) {
            estimatorData.value.buildProposalData[section].content = defaultValue || '';
        }
    };

    /**
     * Load build proposal data from existing estimate response
     * Populates buildProposalData with settings from EstimatorSettings store and estimate response
     * @param {Object} estimateResponse - The estimate response object containing eSign and other data
     */
    const loadBuildProposalDataFromEstimate = (estimateResponse) => {
        // Load data from EstimatorSettings store (where AboutUs, TermsAndConditions, etc. are stored)
        try {
            const settingsStore = getSettingsStore();
            const companySetting = settingsStore.estimateCompanySetting || {};
            
            // Only load from settings if content is empty (preserve edited content)
            setDefaultContent('aboutUs', companySetting.AboutUs);
            setDefaultContent('TermsAndConditions', companySetting.TermsAndConditions);
            setDefaultContent('ScopeOfWork', companySetting.ScopeOfWork);
            setDefaultContent('customNotes', companySetting.CustomNotes);
            
            // Update cover page images if available - default first image as selected
            if (settingsStore.estimateCompanyCoverPageImages && settingsStore.estimateCompanyCoverPageImages.length > 0) {
                const existingImages = estimatorData.value.buildProposalData.coverPageLogoContactInfo.coverPage.imageCoverPage || [];
                // Only initialize if no selection exists
                if (existingImages.length === 0 || !existingImages.some(img => img.isSelectedImage)) {
                    estimatorData.value.buildProposalData.coverPageLogoContactInfo.coverPage.imageCoverPage = 
                        settingsStore.estimateCompanyCoverPageImages.map((img, index) => ({ 
                            ImageURI: img.ImageURI,
                            isSelectedImage: index === 0  // Default first image as selected
                        }));
                }
            }
            
            // Update testimonials if available and not already set
            if (settingsStore.testimonials && (!estimatorData.value.buildProposalData.testimonials.content || estimatorData.value.buildProposalData.testimonials.content.length === 0)) {
                estimatorData.value.buildProposalData.testimonials.content = settingsStore.testimonials || [];
            }

            // Load eSign data into estimatorData.buildProposalData.eSign
            if (estimateResponse) {
                estimatorData.value.buildProposalData.eSign.envelopeId = estimateResponse.EnvelopeId || null;
                estimatorData.value.buildProposalData.eSign.homeOwner = {
                    email: estimateResponse.HomeOwnerEmail || '',
                    firstname: estimateResponse.HomeOwnerFirstName || '',
                    lastname: estimateResponse.HomeOwnerLastName || ''
                };
                // Company representative is already pre-populated from userStore in the initial data
            }
        } catch (error) {
            console.error('[createEstimate] Error loading settings from store:', error);
        }
    };

    // Initialize user details in eSign section after store is ready
    const initializeUserDetails = () => {
        try {
            if (estimatorData.value?.buildProposalData?.eSign?.companyRepresentative && userStore?.userDetails) {
                estimatorData.value.buildProposalData.eSign.companyRepresentative.email = userStore.userDetails?.Email || '';
                estimatorData.value.buildProposalData.eSign.companyRepresentative.firstname = userStore.userDetails?.FirstName || '';
                estimatorData.value.buildProposalData.eSign.companyRepresentative.lastname = userStore.userDetails?.LastName || '';
            }
        } catch (error) {
            console.error('[createEstimate] Error initializing user details:', error);
        }
    };

    // ===== COVER PAGE AND LOGO ACTIONS =====
    const getCoverPageData = () => {
        return estimatorData.value?.buildProposalData?.coverPageLogoContactInfo?.coverPage || {
            customCoverImageURI: '',
            imageCoverPage: []
        };
    };

    const getSelectedCoverPageImage = () => {
        const coverPage = getCoverPageData();
        return coverPage.imageCoverPage?.[0] || null;
    };

    const setCoverPageImages = (images) => {
        if (estimatorData.value?.buildProposalData?.coverPageLogoContactInfo?.coverPage) {
            estimatorData.value.buildProposalData.coverPageLogoContactInfo.coverPage.imageCoverPage = images || [];
        }
    };

    const setCustomCoverImageURI = (uri) => {
        if (estimatorData.value?.buildProposalData?.coverPageLogoContactInfo?.coverPage) {
            estimatorData.value.buildProposalData.coverPageLogoContactInfo.coverPage.customCoverImageURI = uri || '';
        }
    };

    const getCompanyLogoData = () => {
        return estimatorData.value?.buildProposalData?.coverPageLogoContactInfo?.imageCompanyLogo || {
            companyLogo: []
        };
    };

    const getSelectedCompanyLogo = () => {
        const logoData = getCompanyLogoData();
        return logoData.companyLogo?.[0] || null;
    };

    const setCompanyLogos = (logos) => {
        if (estimatorData.value?.buildProposalData?.coverPageLogoContactInfo?.imageCompanyLogo) {
            estimatorData.value.buildProposalData.coverPageLogoContactInfo.imageCompanyLogo.companyLogo = logos || [];
        }
    };

    // ===== CONTACT INFORMATION =====
    const getContactInformation = () => {
        return estimatorData.value?.buildProposalData?.coverPageLogoContactInfo?.contactInformation || {
            isSelectedAddress: true,
            address: '',
            isSelectedEmailAddress: true,
            emailAddress: '',
            isSelectedPhoneNumber: true,
            phoneNumber: ''
        };
    };

    const updateContactInformationField = (field, value) => {
        if (estimatorData.value?.buildProposalData?.coverPageLogoContactInfo?.contactInformation) {
            estimatorData.value.buildProposalData.coverPageLogoContactInfo.contactInformation[field] = value;
        }
    };

    const setContactAddress = (address) => {
        updateContactInformationField('address', address);
    };

    const setContactEmailAddress = (email) => {
        updateContactInformationField('emailAddress', email);
    };

    const setContactPhoneNumber = (phone) => {
        updateContactInformationField('phoneNumber', phone);
    };

    const setContactAddressVisibility = (isVisible) => {
        updateContactInformationField('isSelectedAddress', isVisible);
    };

    const setContactEmailAddressVisibility = (isVisible) => {
        updateContactInformationField('isSelectedEmailAddress', isVisible);
    };

    const setContactPhoneNumberVisibility = (isVisible) => {
        updateContactInformationField('isSelectedPhoneNumber', isVisible);
    };
    // ===== E-SIGN ACTIONS =====
    const updateESignEnvelopeId = (envelopeId) => {
        if (!estimatorData.value.buildProposalData.eSign) {
            estimatorData.value.buildProposalData.eSign = {
                isSelected: true,
                disableCheckBox: true,
                envelopeId: null,
                homeOwner: { email: '', firstname: '', lastname: '' },
                companyRepresentative: {
                    email: userStore.userDetails?.Email || '',
                    firstname: userStore.userDetails?.FirstName || '',
                    lastname: userStore.userDetails?.LastName || ''
                }
            };
        }
        estimatorData.value.buildProposalData.eSign.envelopeId = envelopeId;
    };

    const updateESignHomeOwner = (homeOwnerData) => {
        if (!estimatorData.value.buildProposalData.eSign) {
            estimatorData.value.buildProposalData.eSign = {
                isSelected: true,
                disableCheckBox: true,
                envelopeId: null,
                homeOwner: { email: '', firstname: '', lastname: '' },
                companyRepresentative: {
                    email: userStore.userDetails?.Email || '',
                    firstname: userStore.userDetails?.FirstName || '',
                    lastname: userStore.userDetails?.LastName || ''
                }
            };
        }
        estimatorData.value.buildProposalData.eSign.homeOwner = {
            email: homeOwnerData.email || '',
            firstname: homeOwnerData.firstname || '',
            lastname: homeOwnerData.lastname || ''
        };
    };

    const updateESignCompanyRep = (companyRepData) => {
        if (!estimatorData.value.buildProposalData.eSign) {
            estimatorData.value.buildProposalData.eSign = {
                isSelected: true,
                disableCheckBox: true,
                envelopeId: null,
                homeOwner: { email: '', firstname: '', lastname: '' },
                companyRepresentative: {
                    email: userStore.userDetails?.Email || '',
                    firstname: userStore.userDetails?.FirstName || '',
                    lastname: userStore.userDetails?.LastName || ''
                }
            };
        }
        estimatorData.value.buildProposalData.eSign.companyRepresentative = {
            email: companyRepData.email || '',
            firstname: companyRepData.firstname || '',
            lastname: companyRepData.lastname || ''
        };
    };

    const getESignData = () => estimatorData.value.buildProposalData?.eSign || null;

    // Validate material costs - returns array of validation errors
    // OLD UI behavior: Only check if at least one product exists
    // Does NOT validate quantity > 0 or missing options (matching old UI)
    const validateMaterialCosts = () => {
        const errors = [];
        
        // Check if at least one product exists (same as old UI :disabled="!haveAtLeastOneProduct")
        if (!hasAtLeastOneProduct.value) {
            errors.push({
                type: 'no_products',
                message: 'At least one product is required'
            });
        }

        // OLD UI does NOT validate quantity or missing options - removed to match old behavior

        materialCostValidationErrors.value = errors;
        return errors;
    };

    // ===== hasPricingPermission =====
    const hasPricingPermission = computed(() => userStore.userRoles?.includes('PriceViewer'));

    // ===== ESTIMATE COMPUTED =====
    const activeTiers = computed(() => {
        if (!buildEstimateTiers.value || !Array.isArray(buildEstimateTiers.value)) {
            return [];
        }
        
        return buildEstimateTiers.value.filter(tier => {
            // Filter out category items and items without product selection
            const validMaterialCosts = (tier.materialCosts || []).filter(item => {
                if (item.isCategory) return false;
                if (!item.item?.ProductId && !item.catalogInfo?.ProductId) return false;
                
                const quantity = item.item?.Quantity || 0;
                return quantity > 0;
            });
            
            const validCustomMaterialCosts = (tier.customMaterialCosts || []).filter(item => {
                const quantity = item.quantity || 0;
                return quantity > 0;
            });
            
            return validMaterialCosts.length > 0 || validCustomMaterialCosts.length > 0;
        });
    });

    const hasMultipleTiers = computed(() => {
        return activeTiers.value.length > 1;
    });

    // ===== ESTIMATE METHODS =====
    const status = {
        GENERATED: 1,
        PRESENTED: 2,
        SIGNED: 3,
        JOB_LOST: 4,
        ABANDON: 5,
        DRAFT: 6
    }
    
    const getSelectedEstimateCoverImageUrl = () => {
        const coverPageData = estimatorData.value?.buildProposalData?.coverPageLogoContactInfo?.coverPage;
        const settingsStore = getSettingsStore();
        
        // 1. Check for custom uploaded cover image
        if (coverPageData?.customCoverImageURI) {
            return coverPageData.customCoverImageURI;
        }
        
        // 2. Check for selected image in imageCoverPage array
        if (coverPageData?.imageCoverPage && coverPageData.imageCoverPage.length > 0) {
            const selectedImage = coverPageData.imageCoverPage.find(img => img.isSelectedImage);
            if (selectedImage) {
                // Handle both ImageURI and uri property names
                return selectedImage.ImageURI || selectedImage.uri || '';
            }
            // Fall back to first image in the array
            const firstImage = coverPageData.imageCoverPage[0];
            if (firstImage) {
                return firstImage.ImageURI || firstImage.uri || '';
            }
        }
        
        // 3. Fall back to first image from settings store
        if (settingsStore?.estimateCompanyCoverPageImages?.length > 0) {
            return settingsStore.estimateCompanyCoverPageImages[0]?.ImageURI || '';
        }
        
        return '';
    };

    const getSelectedEstimateCompanyLogoUrl = () => {
        const logoData = estimatorData.value?.buildProposalData?.coverPageLogoContactInfo?.imageCompanyLogo;
        const settingsStore = getSettingsStore();
        
        if (logoData?.companyLogo && logoData.companyLogo.length > 0) {
            const selectedLogo = logoData.companyLogo.find(logo => logo.isSelectedImage);
            if (selectedLogo) {
                return selectedLogo.ImageURI || selectedLogo.uri || '';
            }
            // Fall back to first logo in the array
            const firstLogo = logoData.companyLogo[0];
            if (firstLogo) {
                return firstLogo.ImageURI || firstLogo.uri || '';
            }
        }
        
        // Fall back to first logo from settings store
        if (settingsStore?.estimateCompanyLogos?.length > 0) {
            return settingsStore.estimateCompanyLogos[0]?.ImageURI || '';
        }
        
        return '';
    };

    // Helper to build EstimateCosts from tier charges
    const buildEstimateCosts = (settingsStore) => {
        const estimateCosts = [];
        const getCostDetails = (tier, itemId, chargeType) => {
            if (itemId.startsWith('standard-')) {
                const standardCostID = parseInt(itemId.replace('standard-', ''));
                const standardCost = settingsStore.estimateStandardCosts?.find(sc => sc.StandardCostID === standardCostID);
                if (standardCost) {
                    return {
                        Type: 'Standard',
                        EstimateCostID: standardCost.StandardCostID,
                        Title: standardCost.Title
                    };
                }
            }
            
            if (itemId.startsWith('settings-custom-')) {
                const customCostID = itemId.replace('settings-custom-', '');
                const customCost = settingsStore.estimateCompanyCustomCosts?.find(c => String(c.CustomCostID) === customCostID);
                if (customCost) {
                    return {
                        Type: 'Custom',
                        EstimateCostID: customCost.CustomCostID,
                        Title: customCost.Title
                    };
                }
            }
            
            const customField = tier.customFields?.[chargeType]?.find(f => f.id === itemId);
            if (customField) {
                const estimateCostTypeID = chargeType === 'labor' ? 1 : 2;
                return {
                    Type: 'AdHoc',
                    EstimateCostID: estimateCostTypeID,
                    Title: customField.name
                };
            }
            
            const estimateCostTypeID = chargeType === 'labor' ? 1 : 2;
            return {
                Type: 'AdHoc',
                EstimateCostID: estimateCostTypeID,
                Title: chargeType === 'labor' ? 'Labor' : 'Other Charge'
            };
        };
        
        buildEstimateTiers.value.forEach(tier => {
            const tierID = tier.id;
            ['labor', 'otherCharges'].forEach(chargeType => {
                const charges = tier.chargeValues?.[chargeType];
                if (!charges) return;

                Object.entries(charges).forEach(([itemId, chargeData]) => {
                    const costPerUnit = parseFloat(chargeData.costPerUnit) || 0;
                    const quantity = parseFloat(chargeData.quantity) || 0;

                    if (costPerUnit * quantity <= 0) return;

                    const costDetails = getCostDetails(tier, itemId, chargeType);
                    estimateCosts.push({
                        EstimateCostID: costDetails.EstimateCostID,
                        TierID: tierID,
                        Type: costDetails.Type,
                        Title: costDetails.Title,
                        CostPerUnit: costPerUnit,
                        Units: quantity
                    });
                });
            });
        });

        return estimateCosts;
    };

    const getProposalIncludeFlags = () => {
        const tiers = buildEstimateTiers.value || [];
        const hasItemizedMaterial = tiers.some(t => t.pricing?.isSelectedItemized?.isSelected && t.pricing?.isSelectedItemized?.isSelectedMaterial);
        const hasItemizedLabor = tiers.some(t => t.pricing?.isSelectedItemized?.isSelected && t.pricing?.isSelectedItemized?.isSelectedLabor);
        const hasItemizedOtherCharges = tiers.some(t => t.pricing?.isSelectedItemized?.isSelected && t.pricing?.isSelectedItemized?.isSelectedOtherCharges);
        const hasAnyItemized = tiers.some(t => t.pricing?.isSelectedItemized?.isSelected);
        const hasDisplayDiscount = tiers.some(t => t.pricing?.isSelectedRolledUp?.isSelected && t.pricing?.isSelectedRolledUp?.isSelectedDisplayDiscount);

        return {
            includeMaterialCosts: hasItemizedMaterial || Boolean(estimatorData.value.buildProposalData.itemizedPricing?.isSelectedMaterialCost),
            includeTax: hasAnyItemized || Boolean(estimatorData.value.buildProposalData.itemizedPricing?.isSelectedTax),
            includeLabor: hasItemizedLabor || Boolean(estimatorData.value.buildProposalData.itemizedPricing?.isSelectedLabor),
            includeOtherCharges: hasItemizedOtherCharges || Boolean(estimatorData.value.buildProposalData.itemizedPricing?.isSelectedOtherCharges),
            includeDiscount: hasDisplayDiscount || Boolean(estimatorData.value.buildProposalData.itemizedPricing?.isSelectedDiscount)
        };
    };
    
    const createEstimatePayload = () => {
        const user = userStore.userDetails;
        const contactName = estimatorData.value.jobInformation.contactName || 
                          ((user?.FirstName && user?.LastName) ? `${user.FirstName} ${user.LastName}` : user?.Email || 'Demo User');
        
        const customerCode = user?.CustomerDetail?.CustomerCode || user?.CompanyId || userStore.customerCode || '';

        // Get selected testimonials only from buildProposalData
        const testimonials = estimatorData.value.buildProposalData.testimonials.isSelected 
            ? (estimatorData.value.buildProposalData.testimonials.content || []).filter(t => t.isSelected)
            : [];

        const manufacturerSellSheets = estimatorData.value.buildProposalData.manufacturerSellSheets?.isSelected
            ? (estimatorData.value.buildProposalData.manufacturerSellSheets.content || []).map((sheet, index) => ({
                publicId: sheet.PublicId,
                itemCode: sheet.ItemCode || '',
                fileName: sheet.FileName || '',
                pageCount: sheet.PageCount || 1,
                sortOrder: index
            }))
            : [];

        // Get estimator settings for EstimateProposal
        const settingsStore = useEstimatorSettingsStore();

        const includeFlags = getProposalIncludeFlags();

        const estimateImages = estimatorData.value.buildProposalData.photos.content.map((img, index) => ({
            EstimateImageID: img.imageId || 0,
            EstimateID: estimatorData.value.estimateId || 0,
            ImageType: 0,
            ImageURI: img.imageURI || '',
            DisplayOrder: img.orderDisplay ?? index,
            Description: img.description || '',
            IsSelectedForProposal: img.isSelected ?? true,
            CreatedBy: '',
            CreatedDate: img.createdDate || new Date().toISOString(),
            ModifiedBy: '',
            ModifiedDate: new Date().toISOString()
        }));

        // Build EstimateCosts
        const estimateCosts = buildEstimateCosts(settingsStore);

        return {
            Estimate: {
                EstimateID: estimatorData.value.estimateId || null,
                RoofHubEstimateNumber: estimatorData.value.roofHubEstimateNumber || 0,
                CustomerCode: customerCode,
                AttributeName: 'unknown',
                BranchCode: estimatorData.value.jobInformation.selectedBranch || '',
                SourceSystem: estimateSourceSystem.value,
                leadNumber: estimateLeadNumber.value,
                ShipToSequenceNumber: estimatorData.value.jobInformation.jobAccount || '',
                ShippingMethod: 'Delivery',
                ShipToAddressOne: estimatorData.value.jobInformation.shipToAddress1 || '',
                ShipToAddressTwo: estimatorData.value.jobInformation.shipToAddress2 || '',
                ShipToAddressThree: '',
                ShipToAddressCity: estimatorData.value.jobInformation.shipToAddressCity || '',
                ShipToAddressState: estimatorData.value.jobInformation.shipToAddressState || '',
                ShipToAddressZipCode: estimatorData.value.jobInformation.shipToAddressZipCode || '',
                RoofPitch: estimatorData.value.jobInformation.roofPitch || '',
                JobName: estimatorData.value.jobInformation.jobName || '',
                InitialEstimateContact: contactName,
                StatusId: estimatorData.value.statusId || status.DRAFT,
                EstimateCostCalculation: [
                    {
                        TierID: 1,
                        MaterialTaxRate: 0,
                        NetProfitGoal: 0,
                        Overhead: 0,
                        DiscountAmount: 0,
                        DiscountLabel: '',
                        IsDiscountPercentage: true
                    },
                    {
                        TierID: 2,
                        MaterialTaxRate: 0,
                        NetProfitGoal: 0,
                        Overhead: 0,
                        DiscountAmount: 0,
                        DiscountLabel: '',
                        IsDiscountPercentage: true
                    },
                    {
                        TierID: 3,
                        MaterialTaxRate: 0,
                        NetProfitGoal: 0,
                        Overhead: 0,
                        DiscountAmount: 0,
                        DiscountLabel: '',
                        IsDiscountPercentage: true
                    }
                ],
                EstimateCosts: estimateCosts,
                EstimateMaterialCosts: [],
                MinTierTotal: null,
                MaxTierTotal: null,
                Pricing: {
                    isSelected: estimatorData.value.buildProposalData.pricing.isSelected,
                    disabledCheckbox: estimatorData.value.buildProposalData.pricing.disabledCheckbox,
                    tiers: [],
                    subtitle: estimatorData.value.buildProposalData.pricing.subtitle
                },
                CustomCoverImageURI: estimatorData.value.buildProposalData.coverPageLogoContactInfo.coverPage.customCoverImageURI || '',
                measurementCategory: estimatorData.value.measurementCategory && Object.keys(estimatorData.value.measurementCategory).length > 0 
                    ? JSON.stringify(estimatorData.value.measurementCategory) 
                    : null,
                aerialReportProvider: estimatorData.value.aerialData.aerialReportProvider || '',
                aerialReportReferenceLink: estimatorData.value.aerialData.aerialReportReferenceLink || '',
                aerialReportReferenceNumber: estimatorData.value.aerialData.aerialReportReferenceNumber || '',
                Testimonials: testimonials,
                ManufacturerSellSheets: manufacturerSellSheets,
                EstimateTemplateAssociation: [
                    { TierID: 1, TemplateID: 0 },
                    { TierID: 2, TemplateID: 0 },
                    { TierID: 3, TemplateID: 0 }
                ],
                PropertyReportId: estimatorData.value.propertyReportId || '',
                EstimateProposal: {
                    EstimateID: estimatorData.value.estimateId || null,
                    IncludeCoverPage: estimatorData.value.buildProposalData.coverPageLogoContactInfo.isSelected,
                    IncludeCompanyLogo: estimatorData.value.buildProposalData.coverPageLogoContactInfo.imageCompanyLogo.companyLogo.length > 0,
                    IncludeAboutUs: estimatorData.value.buildProposalData.aboutUs.isSelected,
                    IncludePricing: estimatorData.value.buildProposalData.pricing.isSelected,
                    IncludeTermsAndConditions: estimatorData.value.buildProposalData.TermsAndConditions.isSelected,
                    IncludeScopeOfWork: estimatorData.value.buildProposalData.ScopeOfWork.isSelected,
                    IncludeContactAddress: estimatorData.value.buildProposalData.coverPageLogoContactInfo.contactInformation.isSelectedAddress,
                    IncludeContactEmail: estimatorData.value.buildProposalData.coverPageLogoContactInfo.contactInformation.isSelectedEmailAddress,
                    IncludeContactPhoneNumber: estimatorData.value.buildProposalData.coverPageLogoContactInfo.contactInformation.isSelectedPhoneNumber,
                    ContactAddress: estimatorData.value.buildProposalData.coverPageLogoContactInfo.contactInformation.address || '',
                    ContactEmail: estimatorData.value.buildProposalData.coverPageLogoContactInfo.contactInformation.emailAddress || '',
                    ContactPhoneNumber: estimatorData.value.buildProposalData.coverPageLogoContactInfo.contactInformation.phoneNumber || '',
                    IncludeCustomNotes: estimatorData.value.buildProposalData.customNotes.isSelected,
                    TermsAndConditions: estimatorData.value.buildProposalData.TermsAndConditions.content || settingsStore.termsAndConditions || '',
                    ScopeOfWork: estimatorData.value.buildProposalData.ScopeOfWork.content || settingsStore.scopeOfWork || '',
                    AboutUs: estimatorData.value.buildProposalData.aboutUs.content || settingsStore.aboutUs || '',
                    CustomNotes: estimatorData.value.buildProposalData.customNotes.content || settingsStore.customNotes || '',
                    CoverImageUrl: getSelectedEstimateCoverImageUrl() || settingsStore.coverImageUrl || '',
                    CompanyLogoUrl: getSelectedEstimateCompanyLogoUrl() || settingsStore.companyLogoUrl || '',
                    SectionDisplayOrder: JSON.stringify(estimatorData.value.buildProposalData.sectionDisplayOrder),
                    IncludePhotos: estimatorData.value.buildProposalData.photos.isSelected,
                    IncludeTestimonials: estimatorData.value.buildProposalData.testimonials.isSelected,
                    IncludeManufacturerSellSheets: estimatorData.value.buildProposalData.manufacturerSellSheets?.isSelected ?? false,
                    IncludeMaterialCosts: includeFlags.includeMaterialCosts,
                    IncludeTax: includeFlags.includeTax,
                    IncludeLabor: includeFlags.includeLabor,
                    IncludeOtherCharges: includeFlags.includeOtherCharges,
                    IncludeDiscount: includeFlags.includeDiscount
                },
                ...(estimateImages.length > 0 && {
                    EstimateImages: estimateImages
                })
            }
        };
    }

    // Keep dirty-check parity with old estimator: compare build estimate + core job info only.
    const createLegacyComparableState = () => {
        const jobInfo = estimatorData.value.jobInformation || {};

        return {
            buildEstimate: {
                ...estimatorData.value.buildEstimate,
                tiers: buildEstimateTiers.value
            },
            jobInformation: {
                jobName: jobInfo.jobName || '',
                jobAccount: jobInfo.jobAccount || '',
                selectedBranch: jobInfo.selectedBranch || '',
                roofPitch: jobInfo.roofPitch || '',
                shipToAddress1: jobInfo.shipToAddress1 || '',
                shipToAddress2: jobInfo.shipToAddress2 || '',
                shipToAddressCity: jobInfo.shipToAddressCity || '',
                shipToAddressState: jobInfo.shipToAddressState || '',
                shipToAddressZipCode: jobInfo.shipToAddressZipCode || ''
            }
        };
    };

    const existingEstimatePayload = ref('');

    const createLegacyComparableSnapshot = () => {
        return JSON.stringify(createLegacyComparableState());
    };

    const hasCreateEstimateUnsavedChanges = computed(() => {
        if (!existingEstimatePayload.value) return false;
        if (estimatorData.value.loading?.isLoading === true) return false;
        const currentSnapshot = createLegacyComparableSnapshot();
        return existingEstimatePayload.value !== currentSnapshot;
    });

    // Capture the current state as the baseline for change tracking
    // Call this after initial data load to prevent false positives
    const captureBaselinePayload = () => {
        existingEstimatePayload.value = createLegacyComparableSnapshot();
    };

    const setTiers = (tierData) => {
        if (Array.isArray(tierData)) {
            buildEstimateTiers.value = tierData;
        }
    };

    const updateTierData = (tierId, data) => {
        if (!buildEstimateTiers.value || !Array.isArray(buildEstimateTiers.value)) {
            return;
        }
        const tier = buildEstimateTiers.value.find(t => t.id === tierId);
        if (tier) {
            Object.assign(tier, data);
        }
    };

    // Apply estimator settings to all tiers 
    const applySettingsToTiers = (settings) => {
        if (!settings || !buildEstimateTiers.value) return;
        
        // Apply NetProfitGoal, TaxRate (materialTaxRate), and Overhead to each tier
        buildEstimateTiers.value.forEach(tier => {
            if (tier.calculateCosts) {
                tier.calculateCosts.netProfitGoal = settings.NetProfitGoal || 0;
                tier.calculateCosts.materialTaxRate = settings.TaxRate || 0;
                tier.calculateCosts.overhead = settings.Overhead || 0;
            }
        });
    };

    const calculateTierTotal = (tier, options = {}) => {
        const includeCustomOverride = options.includeCustomOverride !== false;

        if (!tier) return 0;
        
        // Calculate material costs from actual items (exclude categories and items without products)
        let materialCostTotal = 0;
        if (tier.materialCosts && Array.isArray(tier.materialCosts)) {
            tier.materialCosts.forEach(item => {
                if (item.isCategory) return;
                if (!item.item?.ProductId && !item.catalogInfo?.ProductId) return;
                
                const quantity = parseFloat(item.item?.Quantity) || 0;
                if (quantity < 1) return;
                
                const extPrice = parseFloat(item.pricing?.extPrice) || 
                               (parseFloat(item.pricing?.price) || 0) * quantity ||
                               (parseFloat(item.item?.Price) || 0) * quantity;
                
                materialCostTotal += extPrice;
            });
        }
        
        // Add custom material costs
        let customMaterialCostTotal = 0;
        if (tier.customMaterialCosts && Array.isArray(tier.customMaterialCosts)) {
            tier.customMaterialCosts.forEach(item => {
                const quantity = parseFloat(item.quantity) || 0;
                const price = parseFloat(item.price) || 0;
                customMaterialCostTotal += quantity * price;
            });
        }
        
        // Calculate labor charges from chargeValues
        let laborTotal = 0;
        if (tier.chargeValues?.labor) {
            Object.values(tier.chargeValues.labor).forEach(charge => {
                const costPerUnit = parseFloat(charge.costPerUnit) || 0;
                const quantity = parseFloat(charge.quantity) || 0;
                laborTotal += costPerUnit * quantity;
            });
        }
        
        // Calculate other charges from chargeValues
        let otherChargesTotal = 0;
        if (tier.chargeValues?.otherCharges) {
            Object.values(tier.chargeValues.otherCharges).forEach(charge => {
                const costPerUnit = parseFloat(charge.costPerUnit) || 0;
                const quantity = parseFloat(charge.quantity) || 0;
                otherChargesTotal += costPerUnit * quantity;
            });
        }
        
        const materialTaxRate = parseFloat(tier.calculateCosts?.materialTaxRate) || 0;
        // LEGACY: materialTax = Math.round(materialTax * 100) / 100; - rounds BEFORE adding to totalJobCosts
        const materialTax = Math.round((materialCostTotal + customMaterialCostTotal) * (materialTaxRate / 100) * 100) / 100;
        const labor = laborTotal || parseFloat(tier.calculateCosts?.labor) || 0;
        const otherCharges = otherChargesTotal || parseFloat(tier.calculateCosts?.otherCharges) || 0;
        
        // LEGACY CALCULATION (matching EstimatorLandingPage.cshtml lines 709-716 and ReviewEstimateList.cshtml lines 176-199)
        // Step 1: Calculate total job costs (materials + tax + labor + other charges)
        const totalMaterialCost = materialCostTotal + customMaterialCostTotal;
        const totalJobCosts = totalMaterialCost + materialTax + labor + otherCharges;
        
        // Step 2: Calculate margin percentage (netProfitGoal + overhead)
        const netProfitGoal = parseFloat(tier.calculateCosts?.netProfitGoal) || 0;
        const overhead = parseFloat(tier.calculateCosts?.overhead) || 0;
        const marginPercent = (netProfitGoal + overhead) / 100;
        
        // Step 3: Calculate margin amount (grossProfit)
        // Formula: totalJobCostsByTotalGpm = Math.round(totalJobCostsByTotalGpm * 100) / 100;
        const marginAmount = Math.round(totalJobCosts * marginPercent * 100) / 100;
        let proposalPrice = totalJobCosts + marginAmount;
        
        // Step 4: Apply discount
        const discount = parseFloat(tier.calculateCosts?.discount) || 0;
        const discountSymbol = tier.calculateCosts?.discountSymbol || '%';
        
        if (discountSymbol === '%') {
            proposalPrice = proposalPrice - (proposalPrice / 100 * discount);
        } else {
            proposalPrice = proposalPrice - discount;
        }
        
        // Check for NaN
        if (isNaN(proposalPrice)) {
            console.error('[createEstimateStore] proposalPrice is NaN - Invalid calculation');
            return 0;
        }
        
        const finalTotal = Math.round(proposalPrice * 100) / 100;

        // Custom proposal total should override estimated total when active.
        const customTotal = calculateCustomProposalTotal(tier);
        const finalCustomTotal = customTotal === null ? 0 : Math.max(0, Math.round(customTotal * 100) / 100);
        
        // Store calculated values in tier's calculateCosts for API payload
        if (tier.calculateCosts) {
            tier.calculateCosts.materialCost = totalMaterialCost;
            tier.calculateCosts.materialTax = materialTax;
            tier.calculateCosts.labor = labor;
            tier.calculateCosts.otherCharges = otherCharges;
            tier.calculateCosts.totalJobCosts = totalJobCosts;
            tier.calculateCosts.proposedPrice = finalTotal;
            tier.calculateCosts.customProposedPriceTotal = finalCustomTotal;
        }

        if (includeCustomOverride && tier.calculateCosts?.customProposalPriceIsActive) {
            return finalCustomTotal || 0;
        }

        return finalTotal || 0;
    };

    const calculateTierEstimatedTotal = (tier) => {
        return calculateTierTotal(tier, { includeCustomOverride: false });
    };

    const calculateCustomProposalTotal = (tier) => {
        if (!tier?.calculateCosts?.customProposalPriceIsActive) return null;
        
        if (tier.calculateCosts.customDiscountSymbol === '$') {
            return tier.calculateCosts.customProposalPrice - (tier.calculateCosts.customDiscount || 0);
        } else {
            return tier.calculateCosts.customProposalPrice * ((100 - (tier.calculateCosts.customDiscount || 0)) / 100);
        }
    };

    // Fetch pricing for a specific tier (called after template load)
    const fetchTierPricing = async (tierId) => {
        const tier = getTierById(tierId), branch = estimatorData.value.jobInformation.selectedBranch;
        if (!tier || !branch) return;
        const items = (tier.materialCosts || []).filter(i => !i.isCategory && i.item?.ProductId && (i.item?.Quantity || 0) >= 1);
        const pricingItems = items.map((item, idx) => ({
            orderItemIndex: idx,
            ItemCode: item.item?.SelectedVariant?.itemCode || item.item?.itemCode,
            Quantity: item.item?.Quantity,
            UOM: item.item?.DefaultUOM || 'EA',
            Option: typeof item.item?.SelectedColor === 'string'
                ? item.item.SelectedColor
                : (typeof item.item?.Options?.size === 'string' ? item.item.Options.size : '')
        })).filter(p => p.ItemCode);
        if (pricingItems.length > 0) {
            try {
                const prices = (await productCatalogService.getProductPricing({ itemCodes: pricingItems, branchCode: branch, customerCode: JSON.parse(sessionStorage.getItem('rh_user_details') || '{}')?.CustomerDetail?.CustomerCode, shipToSequenceNumber: estimatorData.value.jobInformation.jobAccount }))?.data?.Data?.Pricing || [];
                pricingItems.forEach(p => { const price = parseFloat((prices.find(pr => pr.ItemCode === p.ItemCode) || prices[0])?.Price || 0); if (items[p.orderItemIndex]) items[p.orderItemIndex].pricing = { price, extPrice: price * p.Quantity }; });
            } catch (e) { console.error('Pricing error:', e); }
        }
        calculateTierTotal(tier);
    };

    // ===== PRO REFERRAL LEAD =====
    // Set when the estimate is started from a Pro Referral lead action.
    const proReferralContext = ref(null);

    // ===== WEATHER LEAD =====
    // Set when the estimate is started from a Weather Lead's "Create Estimate" action.
    const weatherLeadContext = ref(null);

    const estimateSourceSystem = computed(() => {
        if (proReferralContext.value) return ESTIMATE_SOURCE_SYSTEM_PRO_LEAD;
        if (weatherLeadContext.value) return ESTIMATE_SOURCE_SYSTEM_WEATHER_LEAD;
        return ESTIMATE_SOURCE_SYSTEM_ROOF_HUB;
    });

    const estimateLeadNumber = computed(() => {
        if (proReferralContext.value) return proReferralContext.value.leadIdShort || null;
        return weatherLeadContext.value?.leadId || null;
    });

    const setProReferralContext = (context) => {
        proReferralContext.value = context ? { ...context } : null;
    };

    const setWeatherLeadContext = (context) => {
        weatherLeadContext.value = context ? { ...context } : null;
    };

    // Keeps the pro referral lead pointed at the job address, including later edits to a prefilled one.
    const linkProReferralLeadAddress = async () => {
        const context = proReferralContext.value;
        if (!context?.leadId) return;

        const address = fullAddress.value;
        if (!address || address === context.linkedAddress) return;

        const response = await LeadService.updateProReferralLeadAddress({
            leadId: context.leadId,
            address
        });

        if (response.success) {
            proReferralContext.value.linkedAddress = address;
        }
    };

    const saveEstimate = async (payload = null) => {
        if (!payload) {
            payload = createEstimatePayload();
        }
        
        try {
            savingDraft.value = true;
            const response = await createEstimatorService.saveEstimate(payload);
            
            if (response.success && response.data) {
                savedEstimateResponse.value = response.data;
                // Load build proposal data from response and capture initial state
                loadBuildProposalDataFromEstimate(response.data);
                captureBuildProposalInitialState();
                
                const estimateIdValue = response.data.Data?.EstimateId || response.data.EstimateId;
                const estimateNumValue = response.data.Data?.EstimateNumber || response.data.EstimateNumber || '';
                
                // Sync to both estimatorData and computed refs for backward compatibility
                estimatorData.value.estimateId = estimateIdValue;
                estimatorData.value.roofHubEstimateNumber = estimateNumValue;
                // Mark current state as clean after successful save.
                captureBaselinePayload();

                await linkProReferralLeadAddress();
            }
            
            return response;
        } catch (error) {
            console.error('[saveEstimate] Error saving estimate:', error);
            return { success: false, error: error.message };
        } finally {
            savingDraft.value = false;
        }
    };

    const estimateProposalId = ref(null)
    const generateProposalId = async () => {
        try {
            // Validate estimateId exists before making API call
            if (!estimatorData.value.estimateId) {
                console.error('[Generate Proposal Id] No estimate ID available');
                return { success: false, error: 'Estimate must be saved before generating proposal ID' };
            }

            const payload = {
                autoGenerateUniqueIdentifier: true,
                estimateID: estimatorData.value.estimateId,
                tierID: selectedTemplateTier.value || 1, // Defaults to good tier if not set
                uniqueIdentifier: ""
            }
            const response = await estimatorService.generateProposalId(payload);
            if (response.success && response.data) {
                estimateProposalId.value = response.data;
                return { success: true, data: response.data };
            } else {
                const errorMsg = response.error || 'Failed to generate proposal ID';
                console.error('[Generate Proposal Id] API error:', errorMsg);
                console.error('[Generate Proposal Id] Full response on failure:', response);
                return { success: false, error: errorMsg };
            }
        } catch (error) {
            console.error('[Generate Proposal Id] Error generating proposal ID:', error);
            return { success: false, error: error.message || 'An error occurred while generating the proposal ID' };
        }
    }

    const loadingEstimate = ref(false);
    const isDataFullyLoaded = ref(true); // Tracks if estimate data is fully initialized (false during edit load)
    
    const getEstimateById = async (estimateId) => {
        try {
            if (!estimateId) {
                return { success: false, error: 'No estimate ID provided' };
            }
            loadingEstimate.value = true;
            isDataFullyLoaded.value = false; // Block interactions until data is fully loaded
            const response = await createEstimatorService.getEstimateById(estimateId);
            
            if (response.data?.ErrorCode !== 0 || !response.data?.Data) {
                if (response.data?.ErrorCode !== 0) {
                    showToastMessage('error', response.data?.ErrorMessage || 'Error loading estimate');
                }
                return { success: false, error: response.data?.ErrorMessage };
            }

            const data = response.data.Data.Estimate;

            // Set basic estimate information
            estimatorData.value.estimateId = data.EstimateID;
            estimatorData.value.roofHubEstimateNumber = data.RoofHubEstimateNumber;
            estimatorData.value.proposalUniqueID = data.ProposalUniqueID;
            estimatorData.value.statusId = data.StatusId;
            estimatorData.value.propertyReportId = data.propertyReportId;

            // Restore Weather Lead association so subsequent saves don't overwrite it with null
            setWeatherLeadContext(data.leadNumber ? { leadId: data.leadNumber } : null);

            // Parse measurement category
            if (data.measurementCategory) {
                estimatorData.value.measurementCategory = JSON.parse(data.measurementCategory);
            }

            // Set aerial data
            estimatorData.value.aerialData.aerialReportProvider = data.aerialReportProvider;
            estimatorData.value.aerialData.aerialReportReferenceLink = data.aerialReportReferenceLink;
            estimatorData.value.aerialData.aerialReportReferenceNumber = data.aerialReportReferenceNumber;

            // Associate template IDs with tiers and populate tier calculation settings
            buildEstimateTiers.value.forEach(tier => {
                // Associate templateID with tierID
                for (const eta of data.EstimateTemplateAssociation) {
                    if (eta.TierID == tier.id) {
                        tier.templateId = String(eta.TemplateID);

                        // Keep selected tier template metadata aligned for Step 2 labels on existing estimates.
                        const tierKey = tier.id === 1 ? 'good' : (tier.id === 2 ? 'better' : 'best');
                        selectedTierTemplates.value[tierKey] = eta.TemplateID && Number(eta.TemplateID) > 0
                            ? { id: String(eta.TemplateID), name: '' }
                            : null;
                        break;
                    }
                }

                // Populate tier cost calculation settings
                const calculateTierCost = data.EstimateCostCalculation.find(i => i.TierID === tier.id);
                if (calculateTierCost) {
                    tier.calculateCosts.materialTaxRate = calculateTierCost.MaterialTaxRate;
                    tier.calculateCosts.netProfitGoal = calculateTierCost.NetProfitGoal;
                    tier.calculateCosts.overhead = calculateTierCost.Overhead;
                    tier.calculateCosts.discount = calculateTierCost.DiscountAmount;
                    tier.calculateCosts.discountLabel = calculateTierCost.DiscountLabel;
                    tier.calculateCosts.discountSymbol = calculateTierCost.IsDiscountPercentage === true ? '%' : '$';
                }
            });

            // Populate build proposal data if exists
            if (data.EstimateProposal) {
                await populateBuildProposalFromData(data);

                if (estimatorData.value.buildProposalData.coverPageLogoContactInfo.coverPage) {
                    estimatorData.value.buildProposalData.coverPageLogoContactInfo.coverPage.customCoverImageURI = data.customCoverImageURI;
                }
            }

            // Populate job information
            populateJobInformationFromData(data);

            // Populate tier chargeValues from estimate data (for tier-based UI)
            populateTierChargesFromData(data.EstimateCosts);

            // Populate custom proposal prices for tiers
            populateCustomProposalPriceFromData(data);

            // Populate material costs
            await populateMaterialCostsFromData(data);

            // Resolve missing template names once template catalog is available for this branch.
            const selectedBranchCode = estimatorData.value.jobInformation.selectedBranch;
            if (selectedBranchCode) {
                await fetchTemplates(selectedBranchCode);

                ['good', 'better', 'best'].forEach((tierKey) => {
                    const selectedTemplate = selectedTierTemplates.value[tierKey];
                    if (selectedTemplate?.id && !selectedTemplate.name) {
                        const templateInfo = getTemplateById(selectedTemplate.id);
                        if (templateInfo?.name) {
                            setSelectedTierTemplate(tierKey, selectedTemplate.id, templateInfo.name);
                        }
                    }
                });
            }

            // Calculate cost data for all tiers
            initPricingTierData();
            calculateCostDataForAllTiers();

            // Capture loaded estimate state for change tracking
            captureBaselinePayload();

            // Mark data as fully loaded - safe to interact now
            isDataFullyLoaded.value = true;
            
            return { success: true };
        } catch (error) {
            console.error('Error fetching estimate:', error);
            showToastMessage('error', 'Error loading estimate: ' + error.message);
            isDataFullyLoaded.value = true; // Unblock UI even on error
            return { success: false, error: error.message };
        } finally {
            loadingEstimate.value = false;
        }
    }

    // Helper: Populate Build Proposal from estimate data
    const populateBuildProposalFromData = async (dataRoot) => {
        const data = dataRoot.EstimateProposal;
        
        if (data) {
            // Preserve existing propertyReport if it exists, or initialize it
            let existingPropertyReport = estimatorData.value.buildProposalData?.propertyReport || {
                reportId: null,
                reportData: null,
                loading: false
            };
            
            // If there's a propertyReportId in the data and it's different from current, load it
            if (dataRoot.propertyReportId && dataRoot.propertyReportId !== existingPropertyReport.reportId) {
                existingPropertyReport = {
                    reportId: dataRoot.propertyReportId,
                    reportData: null,
                    loading: false
                };
                
                // Load the report details asynchronously
                (async () => {
                    try {
                        const propertyReportsService = (await import('@/services/propertyReportsService')).default;
                        const response = await propertyReportsService.details(dataRoot.propertyReportId);
                        if (response.success && response.data && estimatorData.value.buildProposalData?.propertyReport) {
                            estimatorData.value.buildProposalData.propertyReport.reportData = response.data;
                        }
                    } catch (error) {
                        console.error('Failed to load property report details:', error);
                    }
                })();
            }
            
            // Get settings store to load company logos and cover page images
            const settingsStore = getSettingsStore();
            
            estimatorData.value.buildProposalData = {
                sectionDisplayOrder: data.SectionDisplayOrder ? JSON.parse(data.SectionDisplayOrder) : [],
                aboutUs: {
                    isSelected: data.IncludeAboutUs,
                    content: data.AboutUs || ""
                },
                TermsAndConditions: {
                    isSelected: data.IncludeTermsAndConditions,
                    content: data.TermsAndConditions || ""
                },
                ScopeOfWork: {
                    isSelected: data.IncludeScopeOfWork,
                    content: (data.ScopeOfWork !== null && data.ScopeOfWork !== undefined && data.ScopeOfWork.trim() !== "")
                        ? data.ScopeOfWork
                        : (estimatorData.value?.buildProposalData?.ScopeOfWork?.content || "")
                },
                customNotes: {
                    isSelected: data.IncludeCustomNotes,
                    content: data.CustomNotes || "",
                    placeholder: "Enter your custom notes..."
                },
                pricing: {
                    isSelected: true,
                    content: "",
                    tiers: dataRoot.Pricing.tiers,
                    disableCheckBox: true,
                    subtitle: dataRoot.Pricing.subtitle || ''
                },
                testimonials: {
                    isSelected: data.IncludeTestimonials,
                    content: dataRoot.testimonials
                },
                itemizedPricing: {
                    isSelected: 0,
                    isSelectedMaterialCost: data.IncludeMaterialCosts,
                    isSelectedTax: data.IncludeTax,
                    isSelectedLabor: data.IncludeLabor,
                    isSelectedOtherCharges: data.IncludeOtherCharges,
                    isSelectedDiscount: data.IncludeDiscount
                },
                coverPageLogoContactInfo: {
                    isSelected: data.IncludeCoverPage,
                    imageCompanyLogo: {
                        companyLogo: settingsStore.estimateCompanyLogos ? [...settingsStore.estimateCompanyLogos] : []
                    },
                    coverPage: {
                        customCoverImageURI: data.CoverImageUrl || "",
                        imageCoverPage: settingsStore.estimateCompanyCoverPageImages ? [...settingsStore.estimateCompanyCoverPageImages] : []
                    },
                    contactInformation: {
                        isSelectedAddress: data.IncludeContactAddress,
                        address: data.ContactAddress || "",
                        isSelectedEmailAddress: data.IncludeContactEmail,
                        emailAddress: data.ContactEmail || "",
                        isSelectedPhoneNumber: data.IncludeContactPhoneNumber,
                        phoneNumber: data.ContactPhoneNumber || "",
                    }
                },
                eSign: {
                    isSelected: data?.ESignIsSelected?.Email ?? true,
                    disableCheckBox: true,
                    homeOwner: {
                        email: '',
                        firstname: '',
                        lastname: ''
                    },
                    envelopeId: null,
                    companyRepresentative: {
                        email: (data?.ESign?.Email ?? userStore.userDetails?.Email) || '',
                        firstname: (data?.ESign?.FirstName ?? userStore.userDetails?.FirstName) || '',
                        lastname: (data?.ESign?.LastName ?? userStore.userDetails?.LastName) || ''
                    }
                },
                photos: {
                    isSelected: data.IncludePhotos,
                    content: dataRoot.EstimateImages ? dataRoot.EstimateImages.map(img => ({
                        imageId: img.EstimateImageID,
                        imageURI: img.ImageURI,
                        thumpURI: img.ImageURI,
                        description: img.Description,
                        isSelected: img.IsSelectedForProposal,
                        orderDisplay: img.DisplayOrder
                    })) : []
                },
                // Sell sheet properties use camelCase JsonProperty names on the wire, but the
                // selection grid works in the PascalCase shape returned by the Cloudinary endpoint.
                manufacturerSellSheets: {
                    isSelected: data.includeManufacturerSellSheets ?? false,
                    content: (dataRoot.manufacturerSellSheets || []).map(sheet => ({
                        PublicId: sheet.publicId,
                        ItemCode: sheet.itemCode,
                        FileName: sheet.fileName,
                        PageCount: sheet.pageCount
                    }))
                },
                // Preserve property report data
                propertyReport: existingPropertyReport
            };

            // Populate tier pricing settings from Pricing.tiers
            if (dataRoot.Pricing?.tiers && Array.isArray(dataRoot.Pricing.tiers)) {
                dataRoot.Pricing.tiers.forEach(pricingTier => {
                    const tier = buildEstimateTiers.value.find(t => t.id === pricingTier.id);
                    if (tier && tier.pricing) {
                        // Populate pricing display settings
                        tier.pricing.rename = pricingTier.rename || '';
                        tier.pricing.description = pricingTier.description || '';
                        
                        // Check if any pricing option is explicitly selected
                        const hasRolledUpSelected = pricingTier.isSelectedRolledUp?.isSelected === true;
                        const hasItemizedSelected = pricingTier.isSelectedItemized?.isSelected === true;
                        const hasDefaultSelected = pricingTier.isSelectedDefault === true;
                        
                        // If no option is selected, default to 'Default'
                        if (!hasRolledUpSelected && !hasItemizedSelected && !hasDefaultSelected) {
                            tier.pricing.isSelectedDefault = true;
                        } else {
                            tier.pricing.isSelectedDefault = hasDefaultSelected;
                        }
                        
                        // Populate rolled up pricing settings
                        if (pricingTier.isSelectedRolledUp) {
                            tier.pricing.isSelectedRolledUp = {
                                isSelected: pricingTier.isSelectedRolledUp.isSelected || false,
                                isSelectedDisplayDiscount: pricingTier.isSelectedRolledUp.isSelectedDisplayDiscount || false
                            };
                        }
                        
                        // Populate itemized pricing settings
                        if (pricingTier.isSelectedItemized) {
                            tier.pricing.isSelectedItemized = {
                                isSelected: pricingTier.isSelectedItemized.isSelected || false,
                                isSelectedMaterial: pricingTier.isSelectedItemized.isSelectedMaterial ?? true,
                                isSelectedLabor: pricingTier.isSelectedItemized.isSelectedLabor ?? true,
                                isSelectedOtherCharges: pricingTier.isSelectedItemized.isSelectedOtherCharges ?? true,
                                isSelectedMaterialHidePricing: pricingTier.isSelectedItemized.isSelectedMaterialHidePricing ?? true,
                                isSelectedLaborHidePricing: pricingTier.isSelectedItemized.isSelectedLaborHidePricing ?? true,
                                isSelectedOtherChargesHidePricing: pricingTier.isSelectedItemized.isSelectedOtherChargesHidePricing ?? true
                            };
                        }
                    }
                });
            }

            // Process cover page images - mark the selected one
            if (estimatorData.value.buildProposalData.coverPageLogoContactInfo.coverPage.imageCoverPage.length > 0 && data.CoverImageUrl) {
                const selectedImage = estimatorData.value.buildProposalData.coverPageLogoContactInfo.coverPage.imageCoverPage.find(i => i.ImageURI === data.CoverImageUrl);
                if (selectedImage) {
                    selectedImage.isSelectedImage = true;
                    estimatorData.value.buildProposalData.coverPageLogoContactInfo.coverPage.imageCoverPage = [selectedImage];
                }
            }

            // Process company logo images - mark the selected one
            if (estimatorData.value.buildProposalData.coverPageLogoContactInfo.imageCompanyLogo.companyLogo.length > 0 && data.CompanyLogoUrl) {
                const selectedLogo = estimatorData.value.buildProposalData.coverPageLogoContactInfo.imageCompanyLogo.companyLogo.find(i => i.ImageURI === data.CompanyLogoUrl);
                if (selectedLogo) {
                    selectedLogo.isSelectedImage = true;
                    estimatorData.value.buildProposalData.coverPageLogoContactInfo.imageCompanyLogo.companyLogo = [selectedLogo];
                }
            }
        }
    };

    // Helper: Populate Job Information from estimate data
    const populateJobInformationFromData = (data) => {
        const jobInfo = estimatorData.value.jobInformation;

        const branch = jobInfo.branchList.find(i => i.BranchCode === data.BranchCode);
        
        if (branch) {
            jobInfo.selectedBranch = branch.BranchCode;
            jobInfo.branchName = branch.BranchName;
        }

        jobInfo.jobAccount = data.ShipToSequenceNumber;
        jobInfo.shipToAddress1 = data.ShipToAddressOne;
        jobInfo.shipToAddress2 = data.ShipToAddressTwo;
        jobInfo.shipToAddressCity = data.ShipToAddressCity;
        jobInfo.shipToAddressState = data.ShipToAddressState;
        jobInfo.shipToAddressZipCode = data.ShipToAddressZipCode;
        jobInfo.roofPitch = data.RoofPitch;
        jobInfo.jobName = data.JobName;
        jobInfo.contactName = data.InitialEstimateContact;

        // Find and set job account name
        const jobAccount = jobInfo.jobAccounts.find(job => job.ShipToSequenceId === jobInfo.jobAccount);
        jobInfo.jobAccountName = jobAccount ? jobAccount.AddressLine2 : '';
    };

    // Helper: Populate tier chargeValues from estimate data
    const populateTierChargesFromData = (estimateCosts) => {
        const settingsStore = getSettingsStore();
        
        // Clear existing chargeValues and customFields for all tiers
        buildEstimateTiers.value.forEach(tier => {
            tier.chargeValues = { labor: {}, otherCharges: {} };
            tier.customFields = { labor: [], otherCharges: [] };
        });
        
        // Process each cost from the estimate
        estimateCosts.forEach(cost => {
            const tier = buildEstimateTiers.value.find(t => t.id === cost.TierID);
            if (!tier) return;
            
            const costPerUnit = parseFloat(cost.CostPerUnit) || 0;
            const quantity = parseFloat(cost.Units) || 0;
            
            // Skip if no value
            if (costPerUnit === 0 && quantity === 0) return;

            // Determine charge type from StandardCostTypeID (1 = labor, 2 = other charges).
            const chargeType = cost.StandardCostTypeID === 2 ? 'otherCharges' : 'labor';

            if (cost.Type === 'Standard') {
                // Standard costs - use 'standard-{id}' as key
                const itemId = `standard-${cost.EstimateCostID}`;
                tier.chargeValues[chargeType][itemId] = {
                    costPerUnit: costPerUnit,
                    quantity: quantity
                };
            } else if (cost.Type === 'Custom') {
                // Company-level custom costs render as 'settings-custom-{CustomCostID}'
                const itemId = `settings-custom-${cost.EstimateCostID}`;
                tier.chargeValues[chargeType][itemId] = {
                    costPerUnit: costPerUnit,
                    quantity: quantity
                };
            } else if (cost.Type === 'AdHoc') {
                // Ad-hoc/Custom costs - create custom field entry
                const customId = `custom-${Math.random().toString(36).substr(2, 9)}`;
                
                // Add to customFields array
                tier.customFields[chargeType].push({
                    id: customId,
                    name: cost.Title,
                    isDeleted: false
                });
                
                // Add to chargeValues with the custom ID
                tier.chargeValues[chargeType][customId] = {
                    costPerUnit: costPerUnit,
                    quantity: quantity
                };
            }
        });
    };

    // Helper: Populate custom proposal prices for tiers
    const populateCustomProposalPriceFromData = (data) => {
        const customProposalPrices = data?.estimateCustomProposalPrice || data?.EstimateCustomProposalPrice || [];

        for (let tierID = 1; tierID <= 3; tierID++) {
            let tier = buildEstimateTiers.value.find(i => i.id === tierID);
            let proposalPrice = customProposalPrices.find(i => Number(i?.tierID ?? i?.TierID ?? i?.tierId) === tierID);

            if (tier && proposalPrice) {
                const isDiscountPct = proposalPrice.isCustomProposalPriceDiscountPercentage ?? proposalPrice.IsCustomProposalPriceDiscountPercentage ?? true;
                const isActive = proposalPrice.isActive ?? proposalPrice.IsActive ?? true;

                tier.calculateCosts.customProposalPrice = parseFloat(proposalPrice.customProposalPrice ?? proposalPrice.CustomProposalPrice) || 0;
                tier.calculateCosts.customDiscount = parseFloat(proposalPrice.customProposalPriceDiscountAmount ?? proposalPrice.CustomProposalPriceDiscountAmount) || 0;
                tier.calculateCosts.customDiscountSymbol = isDiscountPct ? '%' : '$';
                tier.calculateCosts.customProposalPriceIsActive = Boolean(isActive);
                tier.calculateCosts.useCustomPrice = Boolean(isActive);
            }
        }
    };

    // Helper: Create normalized material cost item structure
    // Structure matches MaterialCostTable.vue product row format
    const createMaterialCostItem = (itemResponse, item, variant, selectedUom, tier) => {
        const variants = itemResponse._normalizedVariants || [];
        
        return {
            id: `product-${item.ProductID}-${tier.id}-${Date.now()}`,
            isCategory: false,
            added: true,
            category: itemResponse.categories?.[0]?.categoryName || item.ProductCategory || '',
            // Product catalog info (accessed by MaterialItemRow via product prop)
            catalogInfo: {
                productId: itemResponse.productId || itemResponse.ProductId,
                ProductId: itemResponse.productId || itemResponse.ProductId,
                productName: itemResponse.productName || itemResponse.ProductName,
                ProductName: itemResponse.productName || itemResponse.ProductName,
                manufacturer: itemResponse.manufacturer || itemResponse.Manufacturer,
                Manufacturer: itemResponse.manufacturer || itemResponse.Manufacturer,
                categories: itemResponse.categories,
                variants: variants,
                selectableFeatures: itemResponse.selectableFeatures || itemResponse.SelectableFeatures || ['COLOR']
            },
            // Item properties (used by MaterialItemRow via item prop)
            item: {
                ProductId: itemResponse.productId || itemResponse.ProductId,
                ProductName: itemResponse.productName || itemResponse.ProductName,
                Manufacturer: itemResponse.manufacturer || itemResponse.Manufacturer,
                Quantity: item.Quantity ?? 0,
                UnitPrice: item.Price,
                Price: item.Price,
                WasteFactor: item.WasteFactor || item.ItemOptions?.WasteFactor || 0,
                MeasurementFactor: item.MeasurementFactor || item.ItemOptions?.MeasurementFactor || 1,
                DefaultUOM: selectedUom?.name || selectedUom?.Name || 'EA',
                Options: (() => {
                    const f = normalizeSelectableFeatures(
                        itemResponse.selectableFeatures || itemResponse.SelectableFeatures
                    );
                    return {
                        color: f.hasColor ? (variant?.colorName || '') : '',
                        size: f.hasSize ? (variant?.sizeName || '') : ''
                    };
                })(),
                SelectedVariant: variant,
                SelectedColor: (() => {
                    const f = normalizeSelectableFeatures(
                        itemResponse.selectableFeatures || itemResponse.SelectableFeatures
                    );
                    return f.hasColor ? (variant?.colorName || '') : '';
                })(),
                SelectedSize: (() => {
                    const f = normalizeSelectableFeatures(
                        itemResponse.selectableFeatures || itemResponse.SelectableFeatures
                    );
                    return f.hasSize ? (variant?.sizeName || '') : '';
                })(),
                itemCode: variant?.itemCode || '',
                TemplateItemId: item.TemplateItemId,
                ItemOptions: item.ItemOptions,
                selectableFeatures: itemResponse.selectableFeatures || itemResponse.SelectableFeatures || [],
            },
            // Pricing info (used by MaterialItemRow via pricing prop)
            pricing: {
                price: item.Price,
                extPrice: (item.Price * (item.Quantity ?? 0))
            }
        };
    };

    // Helper: Populate material costs from estimate data
    const populateMaterialCostsFromData = async (data) => {
        const productIds = data.EstimateMaterialCosts.map(i => i.ProductID).filter(id => id !== null);
        const branch = estimatorData.value.jobInformation.branchList.find(
            i => i.BranchCode === estimatorData.value.jobInformation.selectedBranch
        );

        // If branch not found in branchList, use the BranchCode from the data
        const branchCode = branch?.BranchCode || data.BranchCode;        
        
        if (!branchCode) {
            console.error('Branch code not found in branchList or data');
            return;
        }

        // Only fetch products if there are product IDs
        let productsData = [];
        if (productIds.length > 0) {
            const uniqueProductIds = [...new Set(productIds)];
            const byIdsResponse = await productCatalogService.getProductsByIds(branchCode, uniqueProductIds);

            if (byIdsResponse?.success && Array.isArray(byIdsResponse.data) && byIdsResponse.data.length > 0) {
                productsData = byIdsResponse.data;
            } else {
                // Fallback to details API if batch call fails
                const productPromises = uniqueProductIds.map(async (productId) => {
                    const detailsPayload = {
                        BranchCode: branchCode,
                        ExcludeProductsNotAvailable: false,
                        ProductId: productId
                    };
                    const response = await productCatalogService.getProductDetailsById(detailsPayload);
                    if (response?.success && response?.data?.Data) {
                        return response.data.Data;
                    }
                    return null;
                });

                const results = await Promise.all(productPromises);
                productsData = results.filter(product => product !== null);
            }

            if (productsData.length === 0 && productIds.length > 0) {
                console.error('Failed to fetch products');
                return;
            }
        }

        // Populate material costs for each tier
        buildEstimateTiers.value.forEach(tier => {
            tier.materialItemsWithProblems = [];

            // Standard material costs
            tier.materialCosts = data.EstimateMaterialCosts
                .filter(item => item.Type === 'Standard' && item.TierID === tier.id)
                .map(item => {
                    try {
                        const itemResponse = productsData.find(i => i.productId === item.ProductID || i.ProductId === item.ProductID);
                        
                        if (!itemResponse) {
                            tier.materialItemsWithProblems.push({ item: item, reason: 'Product Not Found' });
                            return undefined;
                        }

                        // Set quantity on the item response
                        itemResponse.Quantity = item.Quantity ?? 0;

                        // Normalize variants to have lowercase properties for MaterialItemRow compatibility
                        const rawVariants = itemResponse.variants || itemResponse.Variants || [];
                        const variants = rawVariants.map(v => {
                            // Normalize UOMs array
                            const rawUoms = v.uoms || v.UOMs || [];
                            const normalizedUoms = rawUoms.map(u => ({
                                ...u,
                                name: u.name || u.Name,
                                isDefaultMeasure: u.isDefaultMeasure || u.IsDefaultMeasure
                            }));
                            
                            return {
                                ...v,
                                colorName: v.colorName || v.ColorName,
                                sizeName: v.sizeName || v.SizeName,
                                itemCode: v.itemCode || v.ItemCode,
                                availableAtBranch: v.availableAtBranch || v.AvailableAtBranch,
                                uoms: normalizedUoms,
                                mediaAssets: v.mediaAssets || v.MediaAssets || []
                            };
                        });
                        
                        // Store normalized variants for helper function
                        itemResponse._normalizedVariants = variants;

                        // STRATEGY 1: Try ItemCode match first (most stable key - from development branch)
                        const savedItemCode = item?.ItemOptions?.ItemCode || item?.ItemCode || '';
                        const normalizedSavedItemCode = String(savedItemCode).trim().toLowerCase();

                        let variant = variants.find(v =>
                            normalizedSavedItemCode &&
                            String(v.itemCode || '').trim().toLowerCase() === normalizedSavedItemCode
                        );

                        // STRATEGY 2: If no ItemCode match, use multi-dimension aware matching
                        if (!variant) {
                            const features = itemResponse.selectableFeatures || itemResponse.SelectableFeatures || [];
                            let searchColor = null;
                            let searchSize = null;
                            let useMultiDimensionMatching = false;

                            if (item.SelectedOptions['SIZE']) {
                                // MDS-saved: SIZE key present
                                useMultiDimensionMatching = true;
                                searchSize = item.SelectedOptions['SIZE'];
                                searchColor = extractPureColorForMds(
                                    item.SelectedOptions['COLOR'] || '',
                                    searchSize
                                );
                                const feat = normalizeSelectableFeatures(features);
                                if (!feat.hasColor) searchColor = '';
                                if (!feat.hasSize) searchSize = '';
                            } else {
                                const resolved = resolveLegacySavedOptionForMds({
                                    combinedColor: item.SelectedOptions['COLOR'] || '',
                                    savedSize: '',
                                    variants,
                                    selectableFeatures: features,
                                    productId: item.ProductID
                                });
                                searchColor = resolved.color || '';
                                searchSize = resolved.size || '';
                                useMultiDimensionMatching = !!(searchColor || searchSize);
                            }

                            if (useMultiDimensionMatching && (searchColor || searchSize)) {
                                variant = findVariantByColorSize(
                                    variants,
                                    { color: searchColor, size: searchSize },
                                    false
                                );
                            }

                            if (!variant && (item.SelectedOptions['COLOR'] || '')) {
                                // Final reverse-combined fallback (COLOR+SIZE products when parse missed)
                                const sc = String(item.SelectedOptions['COLOR'] || '').trim().toLowerCase();
                                variant = variants.find(v => {
                                    const vColor = v.colorName || '';
                                    const vSize = v.sizeName || v.AgilitySize || v.agilitySize || '';
                                    const vCombined = vSize ? `${vSize} ${vColor}` : vColor;
                                    if (vCombined.toLowerCase() === sc) return true;
                                    if (vColor.toLowerCase() === sc) return true;
                                    if (vSize && vSize.toLowerCase() === sc) return true;
                                    return false;
                                });
                            }
                        }

                        if (variant) {
                            // Find UOM (case-insensitive); fallback to variant default measure before first UOM.
                            const uoms = variant.uoms || [];
                            const normalizedItemUom = String(item.UOM || '').trim().toLowerCase();
                            const selectedUom = uoms.find(u =>
                                String(u.name || u.Name || '').trim().toLowerCase() === normalizedItemUom
                            ) || uoms.find(u => u.isDefaultMeasure || u.IsDefaultMeasure) || uoms[0];
                            
                            return createMaterialCostItem(itemResponse, item, variant, selectedUom, tier);
                        } else {
                            // Track variant not found issues
                            const duplicate = tier.materialItemsWithProblems.some(i => i.item.ProductID === item.ProductID);
                            
                            if (item.SelectedOptions['COLOR']) {
                                tier.materialItemsWithProblems.push({ 
                                    item: item, 
                                    reason: 'Variant Not Found ' + item.SelectedOptions['COLOR'] + (duplicate ? ' - Duplicate removed' : '') 
                                });
                            } else {
                                const result = [...new Set(Object.keys(item.SelectedOptions))];
                                tier.materialItemsWithProblems.push({ 
                                    item: item, 
                                    reason: 'Color Variant Not Found ' + result + (duplicate ? ' - Duplicate removed' : '') 
                                });
                            }

                            if (duplicate) {
                                return undefined;
                            }

                            // Use first variant as fallback
                            const fallbackVariant = variants[0];
                            const fallbackUom = fallbackVariant?.uoms?.[0] || {};
                            
                            return createMaterialCostItem(itemResponse, item, fallbackVariant, fallbackUom, tier);
                        }
                    } catch (ex) {
                        tier.materialItemsWithProblems.push({ item: item, reason: 'Product Not Available' });
                        return undefined;
                    }
                }).filter(item => item !== undefined);

            // Custom material costs
            tier.customMaterialCosts = data.EstimateMaterialCosts
                .filter(item => item.Type === 'Custom' && item.TierID === tier.id)
                .map(item => ({
                    TierID: item.TierID,
                    Type: item.Type,
                    FromTemplateID: item.FromTemplateID,
                    ProductId: item.ProductID,
                    ProductName: item.ProductName,
                    Category: item.ProductCategory,
                    SelectedVariant: item.SelectedOptions['CUSTOM'],
                    SelectedUoms: item.UOM,
                    UnitPrice: item.Price,
                    Quantity: item.Quantity,
                    description: item.ProductName,
                    itemDescription: item.ProductName,
                    option: item.SelectedOptions['CUSTOM'],
                    uom: item.UOM,
                    price: item.Price,
                    quantity: item.Quantity
                }));
                
            // Populate global catalog store so items show as "added" in catalog
            const globalCatalogStore = useGlobalCatalogStore();
            tier.materialCosts.forEach(materialItem => {
                if (materialItem?.item?.itemCode) {
                    const productItem = {
                        id: materialItem.id,
                        productId: materialItem.item.ProductId,
                        itemCode: materialItem.item.itemCode,
                        tierId: tier.id,
                        addedFromBOM: true,
                        SelectedColor: materialItem.item.SelectedColor,
                        selectedVariant: materialItem.item.SelectedVariant
                    };
                    globalCatalogStore.externalCartForBOM.push(productItem);
                }
            });
        });
    };

    // Helper: Initialize pricing tier data
    const initPricingTierData = () => {
        estimatorData.value.buildProposalData.pricing.tiers = buildEstimateTiers.value.map(item => {
            const originalItem = estimatorData.value.buildProposalData.pricing.tiers.find(tier => tier.id === item.id);
            
            if (originalItem) {
                originalItem.title = item.title;
                return {
                    ...originalItem,
                    calculateCost: item.calculateCosts,
                    customMaterialCosts: item.customMaterialCosts,
                    displayOrder: item.displayOrder,
                    materialCosts: item.materialCosts,
                    materialItemsWithProblems: item.materialItemsWithProblems,
                    templateId: item.templateId
                };
            } else {
                return {
                    id: item.id,
                    title: item.title,
                    calculateCost: item.calculateCosts,
                    rename: item.pricing.rename,
                    description: item.pricing.description,
                    isSelectedDefault: item.pricing.isSelectedDefault,
                    isSelectedRolledUp: item.pricing.isSelectedRolledUp,
                    isSelectedItemized: item.pricing.isSelectedItemized
                };
            }
        });
    };

    // Helper: Calculate cost data for all tiers
    const calculateCostDataForAllTiers = () => {
        buildEstimateTiers.value.forEach(tier => {
            calculateTierTotal(tier);
        });
    };

    const generateEstimate = async (statusId = 1) => {
        try {
            savingDraft.value = true;
            
            const user = userStore.userDetails;
            
            const customerCode = user?.CustomerDetail?.CustomerCode || user?.CompanyId || userStore.customerCode || '';
            
            const contactName = jobInformation.value.contactName || 
                              ((user?.FirstName && user?.LastName) ? `${user.FirstName} ${user.LastName}` : user?.Email || 'Demo User');
            
            // Build EstimateMaterialCosts from material costs for each tier
            const materialCosts = [];
            const customCosts = [];
            
            buildEstimateTiers.value.forEach(tier => {
                const tierID = tier.id;
                
                // Regular material costs
                if (tier.materialCosts && Array.isArray(tier.materialCosts)) {
                    tier.materialCosts.forEach(item => {
                        if (item.isCategory) return;
                        if (!item.item?.ProductId && !item.catalogInfo?.ProductId) return;
                        
                        const quantity = parseFloat(item.item?.Quantity) || 0;
                        if (quantity <= 0) return;
                        
                        const productId = item.item?.ProductId || item.catalogInfo?.ProductId;
                        const productName = item.item?.ProductName || item.catalogInfo?.ProductName || '';
                        const selectedVariant = item.item?.SelectedVariant || item.catalogInfo?.SelectedVariant;
                        const selectedColor = item.item?.SelectedColor || item.item?.Options?.color || selectedVariant?.colorName || '';
                        const selectedSize = item.item?.SelectedSize || item.item?.Options?.size || '';
                        const selectedUoms = item.item?.SelectedUoms || item.catalogInfo?.SelectedUoms;
                        const uom = item.item?.DefaultUOM || item.item?.uom || selectedUoms?.name || selectedUoms?.Name || 'EA';

                        // item.pricing.price is the correct value to use... falling back to item.item in an abundance of caution
                        const fromPricing = parseFloat(item.pricing?.price);
                        const fromUnit = parseFloat(item.item?.UnitPrice);
                        const fromItemPrice = parseFloat(item.item?.Price);
                        const price = Number.isFinite(fromPricing) ? fromPricing
                            : Number.isFinite(fromUnit) ? fromUnit
                            : Number.isFinite(fromItemPrice) ? fromItemPrice
                            : 0;
                        const category = item.category || item.catalogInfo?.Category || '';
                        const templateItemId = item.item?.TemplateItemId || item.catalogInfo?.TemplateItemId || null;
                        const fromTemplateId = item.item?.FromTemplateId || item.catalogInfo?.FromTemplateId || null;

                        // Keep the combined COLOR value for legacy consumers and explicit SIZE for Web.
                        const selectedOptions = {};

                        if (selectedColor && selectedSize) {
                            const pureColor = extractPureColorForMds(selectedColor, selectedSize);
                            selectedOptions.COLOR = `${selectedSize} ${pureColor}`;
                            selectedOptions.SIZE = selectedSize;
                        } else if (selectedColor) {
                            selectedOptions.COLOR = selectedColor;
                        } else if (selectedSize) {
                            selectedOptions.COLOR = selectedSize;
                        }

                        const materialCostItem = {
                            TierID: tierID,
                            ProductID: productId,
                            ProductName: productName,
                            ProductCategory: category,
                            SelectedOptions: selectedOptions,
                            UOM: uom,
                            Price: price,
                            Type: 'Standard',
                            Quantity: quantity
                        };
                        
                        // Only include FromTemplateID and TemplateItemId if they exist
                        if (fromTemplateId !== null) {
                            materialCostItem.FromTemplateID = fromTemplateId;
                        }
                        if (templateItemId !== null) {
                            materialCostItem.TemplateItemId = templateItemId;
                        }
                        
                        materialCosts.push(materialCostItem);
                    });
                }
                
                // Custom material costs
                if (tier.customMaterialCosts && Array.isArray(tier.customMaterialCosts)) {
                    tier.customMaterialCosts.forEach(item => {
                        const quantity = parseFloat(item.Quantity) || parseFloat(item.quantity) || 0;
                        if (quantity <= 0) return;
                        
                        const fromTemplateId = item.FromTemplateId || item.FromTemplateID || null;
                        
                        const customCostItem = {
                            TierID: tierID,
                            ProductID: item.ProductId || item.ProductID || 0,
                            ProductName: item.ProductName || item.description || item.itemDescription || '',
                            ProductCategory: item.Category || '',
                            SelectedOptions: { CUSTOM: item.SelectedVariant || item.option || '' },
                            UOM: item.SelectedUoms || item.uom || 'EA',
                            Price: parseFloat(item.UnitPrice) || parseFloat(item.price) || 0,
                            Type: 'Custom',
                            Quantity: quantity
                        };
                        
                        // Only include FromTemplateID if it exists
                        if (fromTemplateId !== null) {
                            customCostItem.FromTemplateID = fromTemplateId;
                        }
                        
                        customCosts.push(customCostItem);
                    });
                }
            });
            
            const allMaterialCosts = materialCosts.concat(customCosts);
            
            const settingsStore = useEstimatorSettingsStore();

            const includeFlags = getProposalIncludeFlags();
            
            // Build EstimateCosts  
            const estimateCosts = buildEstimateCosts(settingsStore);
            
            // Build EstimateCostCalculation for each tier
            const estimateCostCalculations = buildEstimateTiers.value.map(tier => ({
                TierID: tier.id,
                MaterialTaxRate: parseFloat(tier.calculateCosts?.materialTaxRate) || 0,
                NetProfitGoal: parseFloat(tier.calculateCosts?.netProfitGoal) || 0,
                Overhead: parseFloat(tier.calculateCosts?.overhead) || 0,
                DiscountAmount: parseFloat(tier.calculateCosts?.discount) || 0,
                DiscountLabel: tier.calculateCosts?.discountLabel || '',
                IsDiscountPercentage: tier.calculateCosts?.discountSymbol === '%'
            }));
            
            // Calculate tier totals and store them in each tier's calculateCosts
            // This ensures MinTierTotal and MaxTierTotal are accurate
            buildEstimateTiers.value.forEach(tier => {
                calculateTierTotal(tier);
            });
            
            // Calculate MinTierTotal and MaxTierTotal from active tiers
            let minTierTotal = null;
            let maxTierTotal = null;
            
            activeTiers.value.forEach(tier => {
                const tierTotal = tier.calculateCosts?.customProposalPriceIsActive
                    ? tier.calculateCosts.customProposedPriceTotal
                    : tier.calculateCosts.proposedPrice;
                
                if (tierTotal && tierTotal > 0) {
                    if (minTierTotal === null || tierTotal < minTierTotal) {
                        minTierTotal = tierTotal;
                    }
                    if (maxTierTotal === null || tierTotal > maxTierTotal) {
                        maxTierTotal = tierTotal;
                    }
                }
            });
            
            // Build EstimateTemplateAssociation
            const estimateTemplateAssociation = buildEstimateTiers.value.map(tier => ({
                TierID: Number(tier.id),
                TemplateID: Number(tier.templateId) || 0
            }));
            
            // Build estimateCustomProposalPrice
            const estimateCustomProposalPrice = buildEstimateTiers.value.map(tier => ({
                estimateID: estimateId.value || 0,
                tierID: tier.id,
                customProposalPrice: parseFloat(tier.calculateCosts?.customProposalPrice) || 0,
                customProposalPriceDiscountAmount: parseFloat(tier.calculateCosts?.customDiscount) || 0,
                isCustomProposalPriceDiscountPercentage: tier.calculateCosts?.customDiscountSymbol === '%',
                isActive: tier.calculateCosts?.customProposalPriceIsActive || false
            }));
            
            // Get selected testimonials only from buildProposalData 
            const testimonials = estimatorData.value.buildProposalData.testimonials.isSelected 
                ? (estimatorData.value.buildProposalData.testimonials.content || []).filter(t => t.isSelected)
                : [];

            const manufacturerSellSheets = estimatorData.value.buildProposalData.manufacturerSellSheets?.isSelected
                ? (estimatorData.value.buildProposalData.manufacturerSellSheets.content || []).map((sheet, index) => ({
                    publicId: sheet.PublicId,
                    itemCode: sheet.ItemCode || '',
                    fileName: sheet.FileName || '',
                    pageCount: sheet.PageCount || 1,
                    sortOrder: index
                }))
                : [];

            // Build EstimateImages
            const estimateImages = estimatorData.value.buildProposalData.photos.content.map((img, index) => ({
                EstimateImageID: img.imageId || 0,
                EstimateID: estimatorData.value.estimateId || 0,
                ImageType: 0,
                ImageURI: img.imageURI || '',
                DisplayOrder: img.orderDisplay ?? index,
                Description: img.description || '',
                IsSelectedForProposal: img.isSelected ?? true,
                CreatedBy: '',
                CreatedDate: img.createdDate || new Date().toISOString(),
                ModifiedBy: '',
                ModifiedDate: new Date().toISOString()
            }));
            
            // Build the complete payload 
            const payload = {
                Estimate: {
                    EstimateID: estimatorData.value.estimateId || null,
                    RoofHubEstimateNumber: estimatorData.value.roofHubEstimateNumber || 0,
                    CustomerCode: customerCode,
                    AttributeName: 'unknown',
                    BranchCode: estimatorData.value.jobInformation.selectedBranch,
                    SourceSystem: estimateSourceSystem.value,
                    leadNumber: estimateLeadNumber.value,
                    ShipToSequenceNumber: estimatorData.value.jobInformation.jobAccount,
                    ShippingMethod: 'Delivery',
                    ShipToAddressOne: estimatorData.value.jobInformation.shipToAddress1,
                    ShipToAddressTwo: estimatorData.value.jobInformation.shipToAddress2 || '',
                    ShipToAddressThree: '',
                    ShipToAddressCity: estimatorData.value.jobInformation.shipToAddressCity,
                    ShipToAddressState: estimatorData.value.jobInformation.shipToAddressState,
                    ShipToAddressZipCode: estimatorData.value.jobInformation.shipToAddressZipCode,
                    ShipToLatitude: estimatorData.value.jobInformation.latitude ? String(estimatorData.value.jobInformation.latitude) : null,
                    ShipToLongitude: estimatorData.value.jobInformation.longitude ? String(estimatorData.value.jobInformation.longitude) : null,
                    RoofPitch: estimatorData.value.jobInformation.roofPitch || '',
                    JobName: estimatorData.value.jobInformation.jobName,
                    InitialEstimateContact: contactName,
                    StatusId: statusId, // 1 = Generated, 6 = Draft
                    EstimateCostCalculation: estimateCostCalculations,
                    EstimateCosts: estimateCosts,
                    EstimateMaterialCosts: allMaterialCosts,
                    MinTierTotal: minTierTotal,
                    MaxTierTotal: maxTierTotal,
                    Pricing: {
                        isSelected: estimatorData.value.buildProposalData.pricing.isSelected,
                        disabledCheckbox: estimatorData.value.buildProposalData.pricing.disabledCheckbox,
                        tiers: buildEstimateTiers.value.map(tier => ({
                            id: tier.id,
                            title: tier.title,
                            calculateCost: tier.calculateCosts,
                            rename: tier.pricing?.rename || '',
                            description: tier.pricing?.description || '',
                            isSelectedDefault: tier.pricing?.isSelectedDefault || false,
                            isSelectedRolledUp: tier.pricing?.isSelectedRolledUp || {
                                isSelected: false,
                                isSelectedDisplayDiscount: false
                            },
                            isSelectedItemized: tier.pricing?.isSelectedItemized || {
                                isSelected: false,
                                isSelectedMaterial: true,
                                isSelectedLabor: true,
                                isSelectedOtherCharges: true,
                                isSelectedMaterialHidePricing: true,
                                isSelectedLaborHidePricing: true,
                                isSelectedOtherChargesHidePricing: true
                            }
                        })),
                        subtitle: estimatorData.value.buildProposalData.pricing.subtitle
                    },
                    CustomCoverImageURI: estimatorData.value.buildProposalData.coverPageLogoContactInfo.coverPage.customCoverImageURI || '',
                    measurementCategory: estimatorData.value.measurementCategory && Object.keys(estimatorData.value.measurementCategory).length > 0 
                        ? JSON.stringify(estimatorData.value.measurementCategory) 
                        : null,
                    aerialReportProvider: estimatorData.value.aerialData.aerialReportProvider || '',
                    aerialReportReferenceLink: estimatorData.value.aerialData.aerialReportReferenceLink || '',
                    aerialReportReferenceNumber: estimatorData.value.aerialData.aerialReportReferenceNumber || '',
                    Testimonials: testimonials,
                    ManufacturerSellSheets: manufacturerSellSheets,
                    EstimateTemplateAssociation: estimateTemplateAssociation,
                    PropertyReportId: estimatorData.value.propertyReportId || '',
                    estimateCustomProposalPrice: estimateCustomProposalPrice,
                    EstimateProposal: {
                        EstimateID: estimatorData.value.estimateId || null,
                        IncludeCoverPage: estimatorData.value.buildProposalData.coverPageLogoContactInfo.isSelected,
                        IncludeCompanyLogo: estimatorData.value.buildProposalData.coverPageLogoContactInfo.imageCompanyLogo.companyLogo.length > 0,
                        IncludeAboutUs: estimatorData.value.buildProposalData.aboutUs.isSelected,
                        IncludePricing: estimatorData.value.buildProposalData.pricing.isSelected,
                        IncludeTermsAndConditions: estimatorData.value.buildProposalData.TermsAndConditions.isSelected,
                        IncludeScopeOfWork: estimatorData.value.buildProposalData.ScopeOfWork.isSelected,
                        IncludeContactAddress: estimatorData.value.buildProposalData.coverPageLogoContactInfo.contactInformation.isSelectedAddress,
                        IncludeContactEmail: estimatorData.value.buildProposalData.coverPageLogoContactInfo.contactInformation.isSelectedEmailAddress,
                        IncludeContactPhoneNumber: estimatorData.value.buildProposalData.coverPageLogoContactInfo.contactInformation.isSelectedPhoneNumber,
                        ContactAddress: estimatorData.value.buildProposalData.coverPageLogoContactInfo.contactInformation.address || '',
                        ContactEmail: estimatorData.value.buildProposalData.coverPageLogoContactInfo.contactInformation.emailAddress || '',
                        ContactPhoneNumber: estimatorData.value.buildProposalData.coverPageLogoContactInfo.contactInformation.phoneNumber || '',
                        IncludeMaterialCosts: includeFlags.includeMaterialCosts,
                        IncludeTax: includeFlags.includeTax,
                        IncludeLabor: includeFlags.includeLabor,
                        IncludeOtherCharges: includeFlags.includeOtherCharges,
                        IncludeDiscount: includeFlags.includeDiscount,
                        IncludeCustomNotes: estimatorData.value.buildProposalData.customNotes.isSelected,
                        TermsAndConditions: estimatorData.value.buildProposalData.TermsAndConditions.content || settingsStore.termsAndConditions || '',
                        ScopeOfWork: estimatorData.value.buildProposalData.ScopeOfWork.content || settingsStore.scopeOfWork || '',
                        AboutUs: estimatorData.value.buildProposalData.aboutUs.content || settingsStore.aboutUs || '',
                        CustomNotes: estimatorData.value.buildProposalData.customNotes.content || settingsStore.customNotes || '',
                        CoverImageUrl: getSelectedEstimateCoverImageUrl() || settingsStore.coverImageUrl || '',
                        CompanyLogoUrl: getSelectedEstimateCompanyLogoUrl() || settingsStore.companyLogoUrl || '',
                        SectionDisplayOrder: JSON.stringify(estimatorData.value.buildProposalData.sectionDisplayOrder),
                        IncludePhotos: estimatorData.value.buildProposalData.photos.isSelected,
                        IncludeTestimonials: estimatorData.value.buildProposalData.testimonials.isSelected,
                        IncludeManufacturerSellSheets: estimatorData.value.buildProposalData.manufacturerSellSheets?.isSelected ?? false
                    },
                    ...(estimateImages.length > 0 && {
                        EstimateImages: estimateImages
                    })
                }
            };
            
            const response = await createEstimatorService.saveEstimate(payload);
            
            if (response.success && response.data?.ErrorCode === 0) {
                // Handle response data structure
                const responseData = response.data.Data || response.data;
                estimatorData.value.estimateId = responseData.EstimateId;
                estimatorData.value.roofHubEstimateNumber = responseData.RoofHubEstimateNumber;
                estimatorData.value.statusId = statusId;

                // Mark current state as clean after successful save.
                captureBaselinePayload();

                await linkProReferralLeadAddress();
                
                return { 
                    success: true, 
                    data: response.data
                };
            }
            
            return response;
        } catch (error) {
            console.error('Error generating estimate:', error);
            return { success: false, error: error.message };
        } finally {
            savingDraft.value = false;
        }
    };

    // ===== STEP NAVIGATION METHODS =====
    const setCurrentStep = (step) => {
        const foundStep = steps.find(s => s.step === step);
        if (foundStep) {
            currentStep.value = foundStep;
        }
    };

    const goToNextStep = () => {
        const currentStepNum = currentStep.value.step;
        if (canProceedToNextStep.value && currentStepNum < steps.length) {
            const nextStep = steps.find(s => s.step === currentStepNum + 1);
            if (nextStep) {
                currentStep.value = nextStep;
            }
        }
    };

    const goToPreviousStep = () => {
        const currentStepNum = currentStep.value.step;
        if (currentStepNum > 1) {
            const prevStep = steps.find(s => s.step === currentStepNum - 1);
            if (prevStep) {
                currentStep.value = prevStep;
            }
        }
    };

    // ===== VALIDATION METHODS =====
    const validateAll = () => {
        addressErrors.value = {
            shipToAddress1: !address.value.shipToAddress1 || String(address.value.shipToAddress1).trim() === '',
            shipToAddressCity: !address.value.shipToAddressCity || String(address.value.shipToAddressCity).trim() === '',
            shipToAddressState: !address.value.shipToAddressState || String(address.value.shipToAddressState).trim() === '',
            shipToAddressZipCode: !address.value.shipToAddressZipCode || String(address.value.shipToAddressZipCode).trim() === '' || !createEstimatorService.validateZipCode(address.value.shipToAddressZipCode)
        };

        jobInfoErrors.value = {
            branchCode: !jobInformation.value.branchCode || String(jobInformation.value.branchCode).trim() === '',
            jobAccountId: !jobInformation.value.jobAccountId || String(jobInformation.value.jobAccountId).trim() === '',
            jobName: !jobInformation.value.jobName || String(jobInformation.value.jobName).trim() === ''
        };

        return hasValidAddress.value && hasValidJobInfo.value;
    };

    // Clear individual address field errors when values are updated
    const clearAddressFieldError = (field) => {
        if (addressErrors.value[field]) {
            addressErrors.value[field] = false;
        }
    };

    // Clear individual job info field errors when values are updated
    const clearJobInfoFieldError = (field) => {
        if (jobInfoErrors.value[field]) {
            jobInfoErrors.value[field] = false;
        }
    };

    // ===== RESET METHODS =====
    const resetBuildProposalData = () => {
        // Reset buildProposalData within estimatorData to its initial state
        estimatorData.value.buildProposalData.sectionDisplayOrder = [];
        estimatorData.value.buildProposalData.photos.content = [];
        estimatorData.value.buildProposalData.testimonials.content = [];
        estimatorData.value.buildProposalData.aboutUs.content = "";
        estimatorData.value.buildProposalData.pricing.tiers = [];
        estimatorData.value.buildProposalData.TermsAndConditions.content = '';
        estimatorData.value.buildProposalData.ScopeOfWork.content = '';
        estimatorData.value.buildProposalData.customNotes.content = "";
        estimatorData.value.buildProposalData.manufacturerSellSheets.isSelected = false;
        estimatorData.value.buildProposalData.manufacturerSellSheets.content = [];
        estimatorData.value.buildProposalData.eSign.envelopeId = null;
        estimatorData.value.buildProposalData.eSign.homeOwner = {
            email: '',
            firstname: '',
            lastname: ''
        };
        estimatorData.value.buildProposalData.eSign.companyRepresentative = {
            email: userStore.userDetails?.Email || '',
            firstname: userStore.userDetails?.FirstName || '',
            lastname: userStore.userDetails?.LastName || ''
        };
        estimatorData.value.buildProposalData.coverPageLogoContactInfo.coverPage.customCoverImageURI = "";
        estimatorData.value.buildProposalData.coverPageLogoContactInfo.coverPage.imageCoverPage = [];
        estimatorData.value.buildProposalData.coverPageLogoContactInfo.imageCompanyLogo.companyLogo = [];
    };

    const resetAll = () => {
        resetAddress();
        resetJobInformation();
        resetBuildProposalData();
        currentStep.value = steps[0];
        branches.value = [];
        jobAccounts.value = [];
        roofPitchValues.value = [];
        captureBaselinePayload();
    };

    // ===== Toast =====
    const showToast = shallowRef(false);
    const toastType = shallowRef('success');
    const toastMessage = shallowRef('');

    function showToastMessage(type, message) {
        toastType.value = type;
        toastMessage.value = message;
        showToast.value = true;
    }

    // ===== RETURN =====
    return {
        // ===== PRIMARY STATE =====
        // Core estimatorData object - all state centralized here
        estimatorData,
        
        // ===== LEGACY COMPATIBILITY REFS =====
        // These computed properties provide backward compatibility for existing components
        // New components should use estimatorData directly
        address,
        jobInformation,
        branches,
        jobAccounts,
        roofPitchValues,
        isManualAddress,
        loadingBranches,
        loadingJobAccounts,
        loadingRoofPitch,
        savingDraft,
        loadingTemplates,
        addressErrors,
        jobInfoErrors,
        showJobInfoValidationError,
        estimateId,
        estimateNumber,
        estimateStatus,
        materialCosts,
        customMaterialCosts,
        materialCostValidationErrors,
        materialCostsBranchCode,
        materialCostsJobAccountId,
        activeTierId,
        selectedTemplateTier,
        estimatorSettings,
        templateCategories,
        allTemplates,
        tierTemplates,
        templatesBranchCode,
        selectedTierTemplates,
        
        // ===== WORKFLOW STATE =====
        steps,
        currentStep,
        showUnsavedDataDialog,
        crcOfLastSave,
        crcOfLastCalculate,
        isClosestBranchSelected,
        
        // ===== TIERS STATE =====
        buildEstimateTiers,
        activeTier,
        activeTiers,
        hasMultipleTiers,
        activeTierMaterialCosts,
        activeTierCustomMaterialCosts,
        activeTierChargeValues,
        activeTierCustomFields,
        
        // ===== COMPUTED VALUES =====
        hasPricingPermission,
        hasValidAddress,
        hasValidJobInfo,
        canProceedToNextStep,
        fullAddress,
        hasUnsavedChanges,
        hasCreateEstimateUnsavedChanges,
        captureBaselinePayload,
        hasAtLeastOneProduct,
        hasUomValidationError,
        incrementUomValidationError,
        decrementUomValidationError,
        resetUomValidationErrorCount,
        materialCostProducts,
        materialCostTotal,
        formattedMaterialCostTotal,
        canProceedToReviewEstimate,
        laborChargesTotal,
        otherChargesTotal,
        totalCharges,
        
        // ===== CALCULATE COSTS =====
        calculateCosts,
        materialTaxRate,
        netProfitGoal,
        overhead,
        materialTax,
        totalJobCosts,
        totalGrossProfitMargin,
        grossProfit,
        proposalPrice,
        customProposalTotal,
        calculatedProfit,
        
        // ===== TOAST NOTIFICATIONS =====
        showToast,
        toastType,
        toastMessage,
        
        // ===== TIER METHODS =====
        setActiveTier,
        getTierById,
        setTierMaterialCosts,
        setTierCustomMaterialCosts,
        setTierChargeValues,
        setTierCustomFields,
        setTierTemplateId,
        setTiers,
        updateTierData,
        applySettingsToTiers,
        calculateTierTotal,
        calculateTierEstimatedTotal,
        calculateCustomProposalTotal,
        fetchTierPricing,
        getTierChargeTotals,
        getTierMaterialCosts,
        getTierItems,
        getTierCostsBreakdown,
        calculateTierChargeTotal,
        updateTierCalculateCostsField,
        
        // ===== CHARGE METHODS =====
        getChargeValue,
        updateChargeValue,
        addCustomField,
        updateCustomFieldName,
        deleteCustomField,
        getActiveCustomFields,
        
        // ===== CALCULATE COSTS METHODS =====
        updateCalculateCosts,
        resetCalculateCosts,
        toggleCustomProposalPrice,
        
        // ===== ADDRESS METHODS =====
        setAddress,
        resetAddress,
        setManualAddressMode,
        setAddressErrors,
        clearAddressErrors,
        clearAddressFieldError,
        
        // ===== JOB INFORMATION METHODS =====
        setJobInformation,
        resetJobInformation,
        setJobInfoErrors,
        clearJobInfoErrors,
        clearJobInfoFieldError,
        
        // ===== BRANCH METHODS =====
        setBranches,
        selectDefaultBranch,
        filterEligibleBranches,
        
        // ===== BRANCH CHANGE AVAILABILITY =====
        branchChangeState,
        hasMaterialCostItems,
        checkBranchChangeAvailability,
        updateTierAvailabilityForBranch,
        resetBranchChangeState,
        setPendingBranchChange,
        
        // ===== JOB ACCOUNT METHODS =====
        setJobAccounts,
        filterJobAccountsByBranch,
        
        // ===== ROOF PITCH METHODS =====
        fetchRoofPitchValues,
        setRoofPitchValues,
        
        // ===== SETTINGS METHODS =====
        fetchEstimatorSettings,
        
        // ===== TEMPLATE METHODS =====
        fetchTemplates,
        setTemplates,
        setTemplateCategories,
        getTemplateById,
        setSelectedTierTemplate,
        clearSelectedTierTemplates,
        loadTemplateDetails,
        loadingTemplateDetails,
        selectedTemplateId,
        loadTemplateSidingMeasurementKeys,
        clearTemplateSidingMeasurementKeys,
        loadingTemplateSidingMeasurementKeys,

        // ===== MATERIAL COST METHODS =====
        setMaterialCosts,
        setCustomMaterialCosts,
        updateMaterialCostsBranchInfo,
        addMaterialCost,
        updateMaterialCost,
        removeMaterialCost,
        addCustomMaterialCost,
        updateCustomMaterialCost,
        removeCustomMaterialCost,
        clearMaterialCosts,
        setMaterialCostValidationErrors,
        clearMaterialCostValidationErrors,
        validateMaterialCosts,
        // Build proposal actions
        updateAboutUs,
        updateTermsAndConditions,
        updateCustomNotes,
        getAboutUs,
        getTermsAndConditions,
        getCustomNotes,
        setPropertyReport,
        removePropertyReport,
        updatePropertyReportStatus,
        getPropertyReport,
        loadBuildProposalDataFromEstimate,
        initializeUserDetails,
        
        getCoverPageData,
        getSelectedCoverPageImage,
        setCoverPageImages,
        setCustomCoverImageURI,
        getCompanyLogoData,
        getSelectedCompanyLogo,
        setCompanyLogos,
        getContactInformation,
        updateContactInformationField,
        setContactAddress,
        setContactEmailAddress,
        setContactPhoneNumber,
        setContactAddressVisibility,
        setContactEmailAddressVisibility,
        setContactPhoneNumberVisibility,
        
        savedEstimateResponse,
        captureBuildProposalInitialState,
        hasBuildProposalChanges,
        generateProposalId,
        estimateProposalId,
        
        // E-Sign actions
        updateESignEnvelopeId,
        updateESignHomeOwner,
        updateESignCompanyRep,
        getESignData,
        
        saveEstimate,
        getEstimateById,
        loadingEstimate,
        isDataFullyLoaded,
        generateEstimate,
        resetEstimate,

        // ===== PRO REFERRAL LEAD =====
        proReferralContext,
        setProReferralContext,

        // ===== WEATHER LEAD =====
        weatherLeadContext,
        setWeatherLeadContext,
        
        // ===== NAVIGATION METHODS =====
        setCurrentStep,
        goToNextStep,
        goToPreviousStep,
        
        // ===== VALIDATION METHODS =====
        validateAll,
        validateBuildProposal,
        clearAddressFieldError,
        clearJobInfoFieldError,
        resetBuildProposalData,
        
        // ===== RESET METHODS =====
        resetAll,
        
        // ===== NOTIFICATION METHODS =====
        showToastMessage
    };
});
